// ✅ Trekkin.app — useEventsStore.jsx v1.11.0
// -----------------------------------------------------------------------------
// 🔹 Base = v1.10.1 (UPLOAD + INSCRIÇÕES + TROFÉUS + LIVE + TRILHO MODO A + ALT)E
// 🔹 Mantém TUDO o que existia nas versões anteriores (Regra de Ouro OK)
// 🔹 v1.10.1:
//    • createEvent aceita/salva `photos` (galeria genérica do evento)
//    • Mantém guideId / guideName / coGuideId / coGuideName / guidePhotoUrl / coGuidePhotoUrl
//    • updateEvent compatível, atualiza todos os campos extra via fallback genérico
// 🔹 v1.11.0:
//    • registerForEvent passa a aceitar 3.º argumento opcional: `options`
//       – options.selectedDays → guardado como `daysSelected` (array de strings)
//       – `isFullEvent` calculado automaticamente com base em meetingPoints
//    • Totalmente retrocompatível: chamadas antigas (eventId, paymentMethod) continuam a funcionar
// ⚠️ Pagamento é SEMPRE realizado fora da app via Stripe Checkout.
// ⚠️ Esta função NÃO cria sessões Stripe.
// ⚠️ A confirmação final é feita APENAS pelo webhook.
// -----------------------------------------------------------------------------

import { create } from "zustand";
import {
  ref,
  onValue,
  off,
  push,
  update,
  get as getDb,
  runTransaction,
  query,
  orderByChild,
  startAt as startAtQuery,
} from "firebase/database";
import { db } from "../firebaseConfig";
import { useAuthStore } from "./useAuthStore";
import { useGroupStore } from "./useGroupStore";
import { usePanelsStore } from "./usePanelsStore";
import { logEvent } from "./useLogsStore";
import { showToast } from "../components/NotifyToast.jsx";
import { listenRtdbListIncremental } from "../utils/rtdbIncrementalList";
import {
  getClaimedEventInvite,
  clearClaimedEventInvite,
} from "../utils/referrals.js";
import { awardEventPaymentXp } from "../utils/gamificationXp.js";
import { isCouponUsed } from "../utils/coupons.js";
import { countReservedTransferSeats } from "../utils/eventTransferSeats.js";
import { resolveEventRegistrationUser } from "../utils/eventRegistrationActor.js";
import { getEventEndTimestamp } from "../utils/eventTime.js";
import { resolveEventDayRoute } from "../utils/eventDayRoute.js";
import { getEventPartnerWaypointIds } from "../utils/eventPartnerAccess.js";
import { fetchAndCacheWaypoints } from "../utils/waypointsCache.js";
import {
  registrationPaidTotal,
  validatePaidRegistrationAdditions,
} from "../utils/eventRegistrationAmendment.js";

const liveRouteAccessLogCache = new Map();
const eventsCacheKey = (uid) => `trekkin.events.cache.v1.${uid}`;

const PRIVATE_EVENT_INDEX_FIELDS = [
  "createdByEmail",
  "invitedUsers",
  "proposalRequestId",
  "proposalGuestEmails",
  "proposalGuestParticipants",
  "proposalPricingMode",
  "proposalBaseParticipants",
  "proposalGroupTotalPrice",
  "proposalSnapshot",
  "proposalHistory",
  "draftVisibleUids",
  "invitedUids",
  "pendingGuestEmails",
];

function toPublicEventProjection(event = {}) {
  const projected = { ...event };
  PRIVATE_EVENT_INDEX_FIELDS.forEach((field) => {
    delete projected[field];
  });
  return projected;
}

function readEventsCache(uid) {
  try {
    const value = JSON.parse(localStorage.getItem(eventsCacheKey(uid)) || "[]");
    return Array.isArray(value) ? value : [];
  } catch {
    return [];
  }
}

function writeEventsCache(uid, events) {
  try {
    const now = Date.now();
    const currentAndFuture = (Array.isArray(events) ? events : []).filter(
      (event) => getEventEndTimestamp(event) >= now
    );
    localStorage.setItem(eventsCacheKey(uid), JSON.stringify(currentAndFuture));
  } catch {}
}

function logLiveRouteAccess(level, message, payload = {}, ttlMs = 10000) {
  try {
    const key = [
      level,
      message,
      payload?.eventId || "noevent",
      payload?.uid || "nouid",
      payload?.groupId || payload?.eventGroupId || "nogroup",
      payload?.role || "norole",
      payload?.status || "nostatus",
    ].join("|");
    const now = Date.now();
    const last = Number(liveRouteAccessLogCache.get(key) || 0);
    if (now - last < ttlMs) return;
    liveRouteAccessLogCache.set(key, now);
    if (liveRouteAccessLogCache.size > 300) {
      const cutoff = now - ttlMs * 3;
      for (const [cacheKey, ts] of liveRouteAccessLogCache.entries()) {
        if (ts < cutoff) liveRouteAccessLogCache.delete(cacheKey);
      }
    }
  } catch {}
  logEvent(level, message, payload);
}

// 🆕 Função para validar se é hoje (mantida para outras regras futuras)
function isToday(ts) {
  try {
    const d = new Date(ts);
    const n = new Date();
    return (
      d.getDate() === n.getDate() &&
      d.getMonth() === n.getMonth() &&
      d.getFullYear() === n.getFullYear()
    );
  } catch {
    return false;
  }
}

function isBackgroundIdleNow() {
  try {
    return (
      typeof document !== "undefined" &&
      document.hidden === true &&
      !usePanelsStore.getState()?.isRecording
    );
  } catch {
    return false;
  }
}

function normalizeEventForMap(id, v = {}) {
  return {
    id,
    ...v,
    routeGPXUrl:
      v.routeGPXUrl ||
      v.routeGpxUrl ||
      v.gpxUrl ||
      v.routeUrl ||
      v.routeFileUrl ||
      v.gpx ||
      null,
    alternativeRoutes: v.alternativeRoutes || null,
    tourRoutes: v.tourRoutes || null,
  };
}

function withEventTimelineIndex(event = {}) {
  const timelineEndAt = getEventEndTimestamp(event);
  return {
    ...event,
    timelineEndAt: Number.isFinite(timelineEndAt) ? timelineEndAt : 0,
  };
}

function currentEventsQuery(path) {
  return query(
    ref(db, path),
    orderByChild("timelineEndAt"),
    startAtQuery(Date.now())
  );
}

function stripUndefinedDeep(value) {
  if (Array.isArray(value)) {
    return value
      .map((item) => stripUndefinedDeep(item))
      .filter((item) => item !== undefined);
  }
  if (value && typeof value === "object") {
    const out = {};
    Object.entries(value).forEach(([k, v]) => {
      const cleaned = stripUndefinedDeep(v);
      if (cleaned !== undefined) out[k] = cleaned;
    });
    return out;
  }
  if (value === undefined) return undefined;
  return value;
}

function findUndefinedPaths(value, basePath = "", acc = []) {
  if (value === undefined) {
    acc.push(basePath || "<root>");
    return acc;
  }
  if (Array.isArray(value)) {
    value.forEach((item, idx) => {
      findUndefinedPaths(item, `${basePath}[${idx}]`, acc);
    });
    return acc;
  }
  if (value && typeof value === "object") {
    Object.entries(value).forEach(([k, v]) => {
      const nextPath = basePath ? `${basePath}.${k}` : k;
      findUndefinedPaths(v, nextPath, acc);
    });
  }
  return acc;
}

// -----------------------------------------------------------------------------
// 📤 HELPERS UPLOAD PARA PHP (iguais v1.2.0)
// -----------------------------------------------------------------------------
const UPLOAD_IMAGE_ENDPOINT =
  "https://www.trekkin.pt/app/app-upload/upload-image.php";
const UPLOAD_GPX_ENDPOINT =
  "https://www.trekkin.pt/app/app-upload/upload-gpx.php";

export async function uploadEventImage(file) {
  try {
    const formData = new FormData();
    formData.append("file", file);

    const res = await fetch(UPLOAD_IMAGE_ENDPOINT, {
      method: "POST",
      body: formData,
    });
    const data = await res.json();
    if (!data.success) throw new Error(data.error || "Erro ao enviar imagem.");
    return data.url;
  } catch (err) {
    console.error("❌ uploadEventImage falhou:", err);
    throw err;
  }
}

export async function uploadGPX(file) {
  try {
    const formData = new FormData();
    formData.append("file", file);

    const res = await fetch(UPLOAD_GPX_ENDPOINT, {
      method: "POST",
      body: formData,
    });
    const data = await res.json();
    if (!data.success) throw new Error(data.error || "Erro ao enviar GPX.");
    return data.url;
  } catch (err) {
    console.error("❌ uploadGPX falhou:", err);
    throw err;
  }
}

// -----------------------------------------------------------------------------
// STORE
// -----------------------------------------------------------------------------
export const useEventsStore = create((set, get) => ({
  // -----------------------------------------------------------
  // ESTADO BASE
  // -----------------------------------------------------------
  events: [],
  eventsListenerRef: null,
  isLoadingEvents: false,

  eventRegistrations: {},
  registrationListenerRefs: {},
  backgroundPausedListeners: null,
  userEvents: {},
  userEventsListenerRef: null,

  currentEventId: null,

  // -----------------------------------------------------------
  // 🔊 LISTEN EVENTS
  // -----------------------------------------------------------
  listenEvents: () => {
    if (isBackgroundIdleNow()) {
      const paused = get().backgroundPausedListeners || { events: false, registrations: [] };
      set({
        backgroundPausedListeners: {
          ...paused,
          events: true,
        },
      });
      return;
    }

    const auth = useAuthStore.getState();
    const user = auth?.user || null;
    const uid = user?.uid || null;
    const role = user?.role?.toLowerCase?.() || "";
    const isPrivileged = role === "admin" || role === "guide";

    if (!uid) {
      console.warn("[events] listenEvents ignorado — sem user");
      return;
    }

    const cachedEvents = readEventsCache(uid);
    if (cachedEvents.length && !(get().events || []).length) {
      set({ events: cachedEvents, isLoadingEvents: false });
    }

    const { eventsListenerRef, userEventsListenerRef } = get();

    if (eventsListenerRef?.__uid === uid) {
      return;
    }

    if (eventsListenerRef) {
      try {
        if (typeof eventsListenerRef.stop === "function") {
          eventsListenerRef.stop();
        } else if (eventsListenerRef.publicRef || eventsListenerRef.invitedRef) {
          if (eventsListenerRef.publicRef) off(eventsListenerRef.publicRef);
          if (eventsListenerRef.invitedRef) off(eventsListenerRef.invitedRef);
        } else {
          off(eventsListenerRef);
        }
      } catch {}
    }

    let userEventsRawCache = {};
    let emitMergedEvents = null;

    if (!userEventsListenerRef || userEventsListenerRef.__uid !== uid) {
      try {
        if (userEventsListenerRef) off(userEventsListenerRef);
      } catch {}
      const ur = ref(db, `/userEvents/${uid}`);
      ur.__uid = uid;
      set({ userEventsListenerRef: ur });
      onValue(
        ur,
        (snap) => {
          const raw = snap.val() || {};
          userEventsRawCache = raw;
          set({ userEvents: raw });
          if (typeof emitMergedEvents === "function") {
            emitMergedEvents();
          }
        },
        (err) => {
          console.warn("[events] erro listenEvents/userEvents:", err);
        }
      );
    }

    if (isPrivileged) {
      const eventsRef = currentEventsQuery("/events");
      const stop = listenRtdbListIncremental(eventsRef, {
        mapItem: normalizeEventForMap,
        sortItems: (arr) => arr.sort((a, b) => (a.startAt || 0) - (b.startAt || 0)),
        onReady: () => set({ isLoadingEvents: false }),
        onData: (arr) => {
          set({ events: arr, isLoadingEvents: false });
          writeEventsCache(uid, arr);
          console.log("[events] snapshot incremental →", arr.length, "eventos");
        },
        onError: (err) => {
          console.warn("[events] erro listenEvents/incremental:", err);
          set({ isLoadingEvents: false });
        },
      });

      set({
        eventsListenerRef: {
          __uid: uid,
          mode: "privileged_incremental",
          stop,
        },
        isLoadingEvents: true,
      });
      return;
    }

    const publicRef = currentEventsQuery("/eventsPublic");
    const invitedRef = ref(db, `/eventsInvited/${uid}`);
    const allEventsRef = currentEventsQuery("/events");
    let publicList = [];
    let invitedList = [];
    let allEventsList = [];
    let partnerWaypointIds = new Set();
    let readyPublic = false;
    let readyInvited = false;
    let readyAllEvents = false;
    let readyPartnerWaypoints = false;

    const isActiveUserEventStatus = (rawStatus) => {
      const s = String(rawStatus || "")
        .trim()
        .toLowerCase();
      if (!s) return false;
      return ![
        "cancelled",
        "canceled",
        "declined",
        "rejected",
        "removed",
        "expired",
      ].includes(s);
    };

    const emit = () => {
      const userEventIds = new Set(
        Object.entries(userEventsRawCache || {})
          .filter(([, ev]) =>
            isActiveUserEventStatus(
              ev?.status || ev?.state || ev?.registrationStatus || ""
            )
          )
          .map(([eventId]) => String(eventId))
      );

      const merged = new Map();
      publicList.forEach((ev) => merged.set(ev.id, ev));
      invitedList.forEach((ev) => merged.set(ev.id, ev));
      allEventsList.forEach((ev) => {
        const isAssociatedPartner = Array.from(getEventPartnerWaypointIds(ev))
          .some((waypointId) => partnerWaypointIds.has(String(waypointId)));
        if (userEventIds.has(String(ev?.id)) || isAssociatedPartner) {
          merged.set(ev.id, ev);
        }
      });
      const arr = Array.from(merged.values()).sort(
        (a, b) => (a.startAt || 0) - (b.startAt || 0)
      );
      writeEventsCache(uid, arr);
      set({
        events: arr,
        isLoadingEvents: !(readyPublic && readyInvited && readyAllEvents && readyPartnerWaypoints),
      });
    };
    emitMergedEvents = emit;

    // Um parceiro associado a um alojamento/refeição precisa de consultar o
    // respetivo evento mesmo enquanto este ainda está em rascunho. Só usamos
    // os waypoints que pertencem ao utilizador autenticado; os restantes
    // rascunhos continuam fora da lista.
    fetchAndCacheWaypoints()
      .then(({ list = [] } = {}) => {
        partnerWaypointIds = new Set(
          list
            .filter((waypoint) => String(waypoint?.partnerUserUid || "") === String(uid))
            .map((waypoint) => String(waypoint?.id || ""))
            .filter(Boolean)
        );
      })
      .catch((error) => {
        console.warn("[events] erro ao obter associações do parceiro:", error);
        partnerWaypointIds = new Set();
      })
      .finally(() => {
        readyPartnerWaypoints = true;
        emit();
      });

    const stopPublic = listenRtdbListIncremental(publicRef, {
      mapItem: normalizeEventForMap,
      onReady: () => {
        readyPublic = true;
        emit();
      },
      onData: (arr) => {
        publicList = arr;
        emit();
      },
      onError: (err) => {
        console.warn("[events] erro listenEvents/public:", err);
        readyPublic = true;
        emit();
      },
    });

    const stopInvited = listenRtdbListIncremental(invitedRef, {
      mapItem: normalizeEventForMap,
      onReady: () => {
        readyInvited = true;
        emit();
      },
      onData: (arr) => {
        invitedList = arr;
        emit();
      },
      onError: (err) => {
        console.warn("[events] erro listenEvents/invited:", err);
        readyInvited = true;
        emit();
      },
    });

    const stopAllEvents = listenRtdbListIncremental(allEventsRef, {
      mapItem: normalizeEventForMap,
      onReady: () => {
        readyAllEvents = true;
        emit();
      },
      onData: (arr) => {
        allEventsList = arr;
        emit();
      },
      onError: (err) => {
        console.warn("[events] erro listenEvents/all-events:", err);
        readyAllEvents = true;
        emit();
      },
    });

    set({
      eventsListenerRef: {
        __uid: uid,
        mode: "public_invited_incremental",
        stop: () => {
          try {
            stopPublic?.();
          } catch {}
          try {
            stopInvited?.();
          } catch {}
          try {
            stopAllEvents?.();
          } catch {}
        },
      },
      isLoadingEvents: true,
    });
  },

  stopListeningEvents: () => {
    const { eventsListenerRef, userEventsListenerRef } = get();
    if (eventsListenerRef) {
      if (typeof eventsListenerRef.stop === "function") {
        eventsListenerRef.stop();
      } else if (eventsListenerRef.publicRef || eventsListenerRef.invitedRef) {
        if (eventsListenerRef.publicRef) off(eventsListenerRef.publicRef);
        if (eventsListenerRef.invitedRef) off(eventsListenerRef.invitedRef);
      } else {
        off(eventsListenerRef);
      }
      set({ eventsListenerRef: null });
    }
    if (userEventsListenerRef) {
      off(userEventsListenerRef);
      set({ userEventsListenerRef: null, userEvents: {} });
    }
  },

  // -----------------------------------------------------------
  // 🔊 LISTEN REGISTRATIONS + AUTO-SYNC MEMBERS → GROUP
  // -----------------------------------------------------------
  listenEventRegistrations: (eventId) => {
    if (!eventId) return;
    if (isBackgroundIdleNow()) {
      const paused = get().backgroundPausedListeners || { events: false, registrations: [] };
      const registrations = new Set(paused.registrations || []);
      registrations.add(eventId);
      set({
        backgroundPausedListeners: {
          ...paused,
          registrations: Array.from(registrations),
        },
      });
      return;
    }

    const { registrationListenerRefs } = get();
    if (registrationListenerRefs[eventId]) return;

    const r = ref(db, `/eventRegistrations/${eventId}`);

    const membershipBlacklist = new Set([
      "cancelled",
      "deleted",
      "removed",
      "expired",
      "declined",
    ]);

    onValue(
      r,
      async (snap) => {
        const raw = snap.val() || {};

        // 1) Guardar inscrições no estado
        set((state) => ({
          eventRegistrations: {
            ...state.eventRegistrations,
            [eventId]: raw,
          },
        }));

        // 1.1) 🎯 XP por inscrição confirmada/paga no evento (+5, idempotente por evento)
        try {
          const currentUser = useAuthStore.getState().user;
          const currentUid = String(currentUser?.uid || "");
          const myReg = currentUid ? raw?.[currentUid] : null;
          if (currentUid && myReg && typeof myReg === "object") {
            const regStatus = String(myReg?.status || "").toLowerCase();
            const payStatus = String(myReg?.payment?.status || "").toLowerCase();
            const isPaymentConfirmed =
              regStatus === "confirmed" ||
              payStatus === "paid" ||
              payStatus === "confirmed";

            if (isPaymentConfirmed) {
              const xpAward = await awardEventPaymentXp(currentUid, eventId);
              if (xpAward?.grantedXp > 0) {
                const confirmationTs = Math.max(
                  Number(myReg?.payment?.paidAt || 0),
                  Number(myReg?.confirmedAt || 0),
                  Number(myReg?.paidAt || 0),
                  Number(myReg?.createdAt || 0)
                );
                // Ao abrir eventos antigos, a migração pode atribuir XP em falta.
                // O crédito mantém-se, mas o aviso só aparece numa confirmação recente.
                if (confirmationTs > 0 && Date.now() - confirmationTs <= 10 * 60 * 1000) {
                  showToast(`+${xpAward.grantedXp} XP por inscrição confirmada no evento.`, "success");
                }
              }
            }
          }
        } catch (e) {
          console.warn("⚠️ Falha ao atribuir XP de inscrição de evento:", e);
        }

        // 2) 🆕 AUTO-SYNC MEMBERSHIP NO GRUPO DO EVENTO
        try {
          const role =
            useAuthStore.getState().user?.role?.toLowerCase() || "free";
          if (role !== "admin" && role !== "guide") {
            return;
          }

          const eventsState = get().events || [];
          const event = eventsState.find((e) => e.id === eventId) || null;
          let groupId = event?.groupId || null;

          if (!groupId) {
            // Sem grupo associado no store → tentar obter do evento
            try {
              const groupRef = ref(db, `events/${eventId}/groupId`);
              const groupSnap = await getDb(groupRef);
              if (groupSnap.exists()) {
                groupId = groupSnap.val();
              }
            } catch (err) {
              console.warn(
                "[events] Não foi possível ler events/{id}/groupId",
                err
              );
            }
          }

          if (!groupId) {
            return;
          }

          const {
            ensureMemberInGroup,
            // ensureNotMemberInGroup, // 👈 NÃO usado v1.7.x (não removemos ninguém)
          } = useGroupStore.getState() || {};

          if (typeof ensureMemberInGroup !== "function") {
            console.warn(
              "[events] ensureMemberInGroup indisponível no useGroupStore"
            );
            return;
          }

          for (const [uid, reg] of Object.entries(raw)) {
            if (!uid) continue;
            const status = (reg?.status || "").toLowerCase();
            if (membershipBlacklist.has(status)) continue;
            try {
              await ensureMemberInGroup(groupId, uid);
            } catch (err) {
              console.warn(
                "[events] ensureMemberInGroup falhou",
                uid,
                err
              );
            }
          }
        } catch (e) {
          console.warn(
            "[events] auto-sync de membros do grupo falhou em listenEventRegistrations",
            e
          );
        }
      },
      () => {}
    );

    set((s) => ({
      registrationListenerRefs: {
        ...s.registrationListenerRefs,
        [eventId]: r,
      },
    }));
  },

  stopListeningEventRegistrations: (eventId) => {
    if (!eventId) return;
    const { registrationListenerRefs } = get();
    const r = registrationListenerRefs[eventId];
    if (r) {
      off(r);
      const clone = { ...registrationListenerRefs };
      delete clone[eventId];
      set({ registrationListenerRefs: clone });
    }
  },

  pauseBackgroundIdleListeners: () => {
    const { eventsListenerRef, userEventsListenerRef, registrationListenerRefs } = get();
    const registrationIds = Object.keys(registrationListenerRefs || {});
    const hadEventsListener = !!eventsListenerRef || !!userEventsListenerRef;

    if (!hadEventsListener && !registrationIds.length) return;

    get().stopListeningEvents();
    registrationIds.forEach((eventId) => {
      get().stopListeningEventRegistrations(eventId);
    });

    set({
      backgroundPausedListeners: {
        events: hadEventsListener,
        registrations: registrationIds,
      },
    });
  },

  resumeBackgroundIdleListeners: () => {
    const paused = get().backgroundPausedListeners;
    if (!paused) return;

    set({ backgroundPausedListeners: null });

    if (paused.events) {
      get().listenEvents();
    }

    (paused.registrations || []).forEach((eventId) => {
      get().listenEventRegistrations(eventId);
    });
  },

  // -----------------------------------------------------------
  // 🆕 CRIAR EVENTO (MODELO COMPLETO DO PanelEventCreate v4.5.0)
  // -----------------------------------------------------------
  createEvent: async (payload) => {
    const auth = useAuthStore.getState();
    const user = auth.user;
    if (!user) throw new Error("Sem utilizador autenticado.");
    const uid = user.uid;
    const email = user.email;

    let fullName =
      auth.usernames?.[uid] ||
      user.name ||
      user.displayName ||
      email ||
      "Guia";

    fullName = fullName.trim().split(" ")[0];

    const {
      // 🔹 Modelo novo do PanelEventCreate v4.5.0
      name,
      activityMode = "walk",
      shortDescription = null,
      longDescription = null,
      shouldBring = null,
      technicalDetails = null,
      includedInfo = null,
      published = false,
      teaserUrl = null,
      extraImages = null,
      inviteOnly = false,
      invitedUsers = null,

      price = 0,
      maxParticipants = null,

      transferPrice = 0,
      transferSeats = 0,

      startAt = Date.now(),
      endAt = null,
      startLocation = null,

      imageUrl: imageUrlDirect = null,
      routeGPXUrl: routeGPXUrlDirect = null,
      routeInputMode = null,

      globalLodgings = null,
      globalMeals = null,
      globalLodgingType = null,
      globalLodgingName = null,
      globalLodgingPrice = null,

      meetingPoints = null,
      proposalPricingMode = null,
      proposalBaseParticipants = null,
      proposalGroupTotalPrice = null,
      proposalGuestEmails = null,
      proposalGuestParticipants = null,
      deferGroupCreation = false,
      visibilityMode = null,
      isPublic = false,

      guideId = null,
      guideName = null,
      guidePhotoUrl = null,
      coGuideId = null,
      coGuideName = null,
      coGuidePhotoUrl = null,

      photos = null, // 🆕 galeria de fotos genérica do evento

      // 🔹 Campos antigos mantidos (para compat / AlwaysRace / etc.)
      description = "",
      distanceKm = 0,
      elevationGain = 0,
      elevationLoss = 0,
      difficulty = null,
      paymentDeadlineHours: payloadPaymentDeadlineHours,
      image,
      coverImage,
      photoUrl,
      trailImage,
      gpxUrl,
      routeUrl,
      routeFileUrl,
      routeGpxUrl,
      gpx,
    } = payload || {};

    if (!name) throw new Error("O evento precisa de um nome.");

    const finalShortDescription =
      shortDescription !== undefined ? shortDescription : null;
    const finalLongDescription =
      longDescription !== undefined ? longDescription : null;
    const finalShouldBring =
      typeof shouldBring === "string"
        ? shouldBring.trim() || null
        : shouldBring ?? null;
    const finalTechnicalDetails =
      typeof technicalDetails === "string"
        ? technicalDetails.trim() || null
        : technicalDetails ?? null;

    // `description` antigo continua a existir, mas se vier vazio usamos shortDescription
    const finalDescription =
      description || finalShortDescription || finalLongDescription || "";

    const imageUrl =
      imageUrlDirect || image || coverImage || photoUrl || trailImage || null;

    const routeGPXUrl =
      routeGPXUrlDirect ||
      routeGpxUrl ||
      gpxUrl ||
      routeUrl ||
      routeFileUrl ||
      gpx ||
      null;

    const pdh =
      typeof payloadPaymentDeadlineHours === "number"
        ? payloadPaymentDeadlineHours
        : 48;

    // Criar grupo automático
    let groupId = null;
    if (!deferGroupCreation) {
      try {
        const { createGroup } = useGroupStore.getState();
        groupId = await createGroup(name);
      } catch (e) {
        throw e;
      }
    }

    const newRef = push(ref(db, "/events"));
    const eventId = newRef.key;
    const now = Date.now();

    const numericPrice =
      typeof price === "number" ? price : Number(price || 0);
    const numericTransferPrice =
      typeof transferPrice === "number"
        ? transferPrice
        : Number(transferPrice || 0);
    const numericTransferSeats =
      typeof transferSeats === "number"
        ? transferSeats
        : Number(transferSeats || 0);
    const numericGlobalLodgingPrice =
      globalLodgingPrice != null && globalLodgingPrice !== ""
        ? Number(globalLodgingPrice)
        : null;

    const hasLegacyGlobalLodging =
      !!globalLodgingName ||
      !!globalLodgingType ||
      (globalLodgingPrice != null && globalLodgingPrice !== "");

    const normalizedMeetingPoints =
      Array.isArray(meetingPoints) && meetingPoints.length
        ? meetingPoints
        : null;
    const normalizedProposalGuestEmails =
      Array.isArray(proposalGuestEmails) && proposalGuestEmails.length
        ? proposalGuestEmails
        : null;
    const normalizedProposalGuestParticipants =
      Array.isArray(proposalGuestParticipants) && proposalGuestParticipants.length
        ? proposalGuestParticipants
        : null;

    const normalizedPhotos =
      Array.isArray(photos) && photos.length ? photos : null;

    const normalizedGlobalLodgings =
      Array.isArray(globalLodgings) && globalLodgings.length
        ? globalLodgings
        : null;
    const normalizedGlobalMeals =
      Array.isArray(globalMeals) && globalMeals.length
        ? globalMeals
        : null;

    const eventData = {
      // Básico
      name,
      activityMode: activityMode === "bike" ? "bike" : "walk",
      description: finalDescription,
      shortDescription: finalShortDescription,
      longDescription: finalLongDescription,
      shouldBring: finalShouldBring,
      technicalDetails: finalTechnicalDetails,

      // Info adicional
      includedInfo: includedInfo || null,
      teaserUrl: teaserUrl || null,
      extraImages:
        Array.isArray(extraImages) && extraImages.length ? extraImages : null,

      // Valores globais do evento
      price: Number.isFinite(numericPrice) ? numericPrice : 0,
      maxParticipants: maxParticipants ?? null,
      transferPrice: Number.isFinite(numericTransferPrice)
        ? numericTransferPrice
        : 0,
      transferSeats: Number.isFinite(numericTransferSeats)
        ? numericTransferSeats
        : 0,

      // Distância / altimetria (mantidos)
      distanceKm,
      elevationGain,
      elevationLoss,
      difficulty,
      routeInputMode: routeInputMode || "gpx",

      startAt,
      endAt,
      startLocation,

      paymentDeadlineHours: pdh,
      createdAt: now,
      createdByUid: uid,
      createdByName: fullName,
      createdByEmail: email,

      groupId,

      status: "open",

      // LIVE
      isLive: false,
      liveStartedAt: null,
      liveFinishedAt: null,

      // Media
      imageUrl,
      routeGPXUrl,

      // Alojamento global
      globalLodgingType: hasLegacyGlobalLodging ? globalLodgingType || null : null,
      globalLodgingName: hasLegacyGlobalLodging ? globalLodgingName || null : null,
      globalLodgingPrice: hasLegacyGlobalLodging
        ? Number.isFinite(numericGlobalLodgingPrice)
          ? numericGlobalLodgingPrice
          : 0
        : null,

      // 🆕 Alojamentos globais multi-entrada
      globalLodgings: normalizedGlobalLodgings,
      globalMeals: normalizedGlobalMeals,

      // Multi-dia
      meetingPoints: normalizedMeetingPoints,
      proposalPricingMode: proposalPricingMode || null,
      proposalBaseParticipants:
        Number.isFinite(Number(proposalBaseParticipants))
          ? Number(proposalBaseParticipants)
          : null,
      proposalGroupTotalPrice:
        Number.isFinite(Number(proposalGroupTotalPrice))
          ? Number(proposalGroupTotalPrice)
          : null,
      proposalGuestEmails: normalizedProposalGuestEmails,
      proposalGuestParticipants: normalizedProposalGuestParticipants,

      // Guias
      guideId: guideId || uid,
      guideName: guideName || fullName,
      guidePhotoUrl: guidePhotoUrl || null,
      coGuideId: coGuideId || null,
      coGuideName: coGuideName || null,
      coGuidePhotoUrl: coGuidePhotoUrl || null,

      // Publicação
      published: !!published,
      isPublic: !!isPublic,
      inviteOnly: !!inviteOnly,
      visibilityMode: visibilityMode || null,
      invitedUsers:
        invitedUsers && typeof invitedUsers === "object" ? invitedUsers : null,

      // Galeria genérica de fotos
      photos: normalizedPhotos,

      // Trilhos alternativos
      alternativeRoutes: null,
    };
    eventData.timelineEndAt = getEventEndTimestamp(eventData);

    const updates = {
      [`events/${eventId}`]: eventData,
      [`userEvents/${uid}/${eventId}`]: {
        role: "guide",
        status: "owner",
        createdAt: now,
      },
    };

    const isInviteOnly = eventData.inviteOnly === true;
    const isPublicEvent =
      !isInviteOnly && eventData.published === true && eventData.isPublic === true;

    if (isPublicEvent) {
      updates[`eventsPublic/${eventId}`] = toPublicEventProjection(eventData);
    } else {
      updates[`eventsPublic/${eventId}`] = null;
    }

    if (isInviteOnly) {
      const invited = eventData.invitedUsers || {};
      Object.keys(invited).forEach((invitedUid) => {
        updates[`eventsInvited/${invitedUid}/${eventId}`] = eventData;
      });
    }

    // A ligação do grupo é auxiliar. Não pode anular a criação do
    // evento se uma regra/permissão do grupo estiver temporariamente atrasada.
    const groupUpdates = {};
    if (groupId) {
      groupUpdates[`groups/${groupId}/activeEventId`] = eventId;
      groupUpdates[`groups/${groupId}/eventPublished`] = eventData.published === true;
      groupUpdates[`groups/${groupId}/eventIsPublic`] = eventData.isPublic === true;
      groupUpdates[`groups/${groupId}/activityMode`] = eventData.activityMode === "bike" ? "bike" : "walk";
    }

    try {
      await update(ref(db), updates);
    } catch (error) {
      logEvent("error", "EVENT_CREATE_CORE_FAILED", {
        eventId,
        groupId,
        code: error?.code || null,
        error: error?.message || String(error),
      });
      // A criação do grupo acontece antes da escrita principal. Se esta
      // falhar, removemos apenas esse grupo novo para não deixar lixo/orfãos.
      if (groupId) {
        try {
          await update(ref(db), { [`groups/${groupId}`]: null });
        } catch {}
      }
      throw error;
    }

    if (groupId) {
      try {
        await update(ref(db), groupUpdates);
      } catch (error) {
        logEvent("warn", "EVENT_CREATE_GROUP_SYNC_FAILED", {
          eventId,
          groupId,
          code: error?.code || null,
          error: error?.message || String(error),
        });
      }
    }

    set({ currentEventId: eventId });

    return { eventId, groupId };
  },

  // -----------------------------------------------------------
  // ✏️ ATUALIZAR EVENTO (mantém compat + suporta campos novos)
  // -----------------------------------------------------------
  updateEvent: async (eventId, payload) => {
    if (!eventId) throw new Error("Evento inválido.");

    const updates = {};
    const handledKeys = new Set();

    // Mantém comportamento antigo para estes campos base
    if (payload.name !== undefined) {
      updates[`events/${eventId}/name`] = payload.name;
      handledKeys.add("name");
    }
    if (payload.description !== undefined) {
      updates[`events/${eventId}/description`] = payload.description;
      handledKeys.add("description");
    }
    if (payload.startAt !== undefined) {
      updates[`events/${eventId}/startAt`] = payload.startAt;
      handledKeys.add("startAt");
    }
    if (payload.price !== undefined) {
      updates[`events/${eventId}/price`] = payload.price;
      handledKeys.add("price");
    }
    if (payload.maxParticipants !== undefined) {
      updates[`events/${eventId}/maxParticipants`] = payload.maxParticipants;
      handledKeys.add("maxParticipants");
    }

    // Imagem: suportar aliases antigos (image, coverImage, photoUrl)
    if (
      payload.imageUrl !== undefined ||
      payload.image !== undefined ||
      payload.coverImage !== undefined ||
      payload.photoUrl !== undefined
    ) {
      updates[`events/${eventId}/imageUrl`] =
        payload.imageUrl ??
        payload.image ??
        payload.coverImage ??
        payload.photoUrl ??
        null;
      handledKeys.add("imageUrl");
      handledKeys.add("image");
      handledKeys.add("coverImage");
      handledKeys.add("photoUrl");
    }

    // GPX: suportar aliases antigos (routeGpxUrl, gpxUrl, routeUrl, routeFileUrl, gpx)
    if (
      payload.routeGPXUrl !== undefined ||
      payload.routeGpxUrl !== undefined ||
      payload.gpxUrl !== undefined ||
      payload.routeUrl !== undefined ||
      payload.routeFileUrl !== undefined ||
      payload.gpx !== undefined
    ) {
      updates[`events/${eventId}/routeGPXUrl`] =
        payload.routeGPXUrl ??
        payload.routeGpxUrl ??
        payload.gpxUrl ??
        payload.routeUrl ??
        payload.routeFileUrl ??
        payload.gpx ??
        null;
      handledKeys.add("routeGPXUrl");
      handledKeys.add("routeGpxUrl");
      handledKeys.add("gpxUrl");
      handledKeys.add("routeUrl");
      handledKeys.add("routeFileUrl");
      handledKeys.add("gpx");
    }

    if (payload.startLocation !== undefined) {
      updates[`events/${eventId}/startLocation`] =
        payload.startLocation || null;
      handledKeys.add("startLocation");
    }
    if (payload.createdByUid !== undefined) {
      updates[`events/${eventId}/createdByUid`] =
        payload.createdByUid || null;
      handledKeys.add("createdByUid");
    }

    // 🔥 NOVO: qualquer outro campo no payload vai direto para events/{id}/{campo}
    if (payload && typeof payload === "object") {
      Object.entries(payload).forEach(([key, value]) => {
        if (handledKeys.has(key)) return;
        updates[`events/${eventId}/${key}`] = value ?? null;
      });
    }

    try {
      const eventsState = get().events || [];
      const current = eventsState.find((e) => e.id === eventId) || {};
      let freshEvent = null;
      let hasLiveIndex = false;
      try {
        const [eventSnap, liveSnap] = await Promise.all([
          getDb(ref(db, `events/${eventId}`)),
          getDb(ref(db, `eventsLive/${eventId}`)),
        ]);
        freshEvent = eventSnap.exists() ? eventSnap.val() || null : null;
        hasLiveIndex = liveSnap.exists();
      } catch {}

      const merged = withEventTimelineIndex({
        ...current,
        ...(freshEvent || {}),
        ...payload,
        id: eventId,
      });
      updates[`events/${eventId}/timelineEndAt`] = merged.timelineEndAt;

      // 🔒 Nunca deixar um update comum desligar LIVE por dados stale do cache local.
      const effectiveLive =
        hasLiveIndex || freshEvent?.isLive === true || merged?.isLive === true;
      if (effectiveLive) {
        merged.isLive = true;
        merged.status = "open";
        if (merged.liveFinishedAt !== null) merged.liveFinishedAt = null;
        if (!merged.liveStartedAt) {
          merged.liveStartedAt = freshEvent?.liveStartedAt || Date.now();
        }
      }

      const normalizeInvited = (raw) => {
        if (!raw) return {};
        if (Array.isArray(raw)) {
          return raw.reduce((acc, entry) => {
            if (entry?.uid) acc[entry.uid] = entry;
            return acc;
          }, {});
        }
        if (typeof raw === "object") return { ...raw };
        return {};
      };

      const prevInvited = normalizeInvited(current.invitedUsers);
      const nextInvited = normalizeInvited(merged.invitedUsers);
      const isInviteOnly = merged.inviteOnly === true;
      const isPublicEvent =
        !isInviteOnly &&
        merged.published === true &&
        merged.isPublic === true;

      const indexUpdates = {};
      const groupProjectionUpdates = {};

      // O grupo automático é uma projeção do evento: mantém nome e visibilidade
      // sincronizados sempre que o evento é editado/publicado.
      const linkedGroupId = String(merged.groupId || "").trim();
      if (linkedGroupId) {
        if (payload.name !== undefined) {
          groupProjectionUpdates[`groups/${linkedGroupId}/name`] = merged.name;
        }
        groupProjectionUpdates[`groups/${linkedGroupId}/eventPublished`] =
          merged.published === true;
        groupProjectionUpdates[`groups/${linkedGroupId}/eventIsPublic`] =
          merged.isPublic === true;
        groupProjectionUpdates[`groups/${linkedGroupId}/activityMode`] =
          merged.activityMode === "bike" ? "bike" : "walk";
      }

      if (isPublicEvent) {
        indexUpdates[`eventsPublic/${eventId}`] = toPublicEventProjection(merged);
        if (effectiveLive) {
          indexUpdates[`eventsPublic/${eventId}/isLive`] = true;
          indexUpdates[`eventsPublic/${eventId}/status`] = "open";
          indexUpdates[`eventsPublic/${eventId}/liveFinishedAt`] = null;
          if (merged.liveStartedAt) {
            indexUpdates[`eventsPublic/${eventId}/liveStartedAt`] =
              merged.liveStartedAt;
          }
        }
      } else {
        indexUpdates[`eventsPublic/${eventId}`] = null;
      }

      if (isInviteOnly) {
        Object.keys(nextInvited).forEach((uid) => {
          indexUpdates[`eventsInvited/${uid}/${eventId}`] = merged;
        });
        Object.keys(prevInvited).forEach((uid) => {
          if (!nextInvited[uid]) {
            indexUpdates[`eventsInvited/${uid}/${eventId}`] = null;
          }
        });
      } else {
        Object.keys(prevInvited).forEach((uid) => {
          indexUpdates[`eventsInvited/${uid}/${eventId}`] = null;
        });
      }

      await update(ref(db), { ...updates, ...indexUpdates });
      // Um guia pode editar o evento sem ter permissão de escrita direta no
      // grupo associado. A projeção do grupo é auxiliar e não pode anular a
      // gravação principal (texto, imagens, publicação, etc.).
      if (Object.keys(groupProjectionUpdates).length) {
        try {
          await update(ref(db), groupProjectionUpdates);
        } catch (groupProjectionError) {
          console.warn(
            "[events] Evento atualizado, mas não foi possível sincronizar a projeção do grupo:",
            groupProjectionError
          );
        }
      }
      // Refletir imediatamente a edição na interface. O listener remoto
      // continua a ser a fonte final, mas não deixa a checkbox de publicação
      // com o valor antigo nem permite reabrir o editor com estado obsoleto.
      set((state) => ({
        events: (state.events || []).map((item) =>
          String(item?.id) === String(eventId) ? { ...item, ...merged, id: eventId } : item
        ),
      }));
    } catch (e) {
      console.error("Erro updateEvent:", e);
      throw e;
    }
  },

  // -----------------------------------------------------------
  // 📝 INSCRIÇÃO NO EVENTO (v1.12.0 — SCHEMA FINAL)
  // -----------------------------------------------------------
  registerForEvent: async (eventId, _legacyPaymentMethod, options = {}) => {
    if (!eventId) throw new Error("Evento inválido.");
  

    const auth = useAuthStore.getState();
    const actor = auth.user;
    const { user, adminTargetUid } = await resolveEventRegistrationUser(
      actor,
      options?.adminTargetUid,
      async (targetUid) => {
        const snapshot = await getDb(ref(db, `users/${targetUid}`));
        return snapshot.exists() ? snapshot.val() : null;
      }
    );
    const uid = user.uid;
    const now = Date.now();

    const birthdate =
      user.birthdate ||
      user.birthday ||
      user.dob ||
      user.dateOfBirth ||
      null;

    const ccNumber =
      user.ccNumber || user.cc || user.citizenCard || null;

    if (!birthdate || !ccNumber) {
      throw new Error(
        "Atualiza o perfil com CC e Data de Nascimento."
      );
    }

    const eventsState = get().events || [];
    let event = eventsState.find((e) => e.id === eventId) || null;
    if (!event) {
      const eventSnap = await getDb(ref(db, `events/${eventId}`));
      event = eventSnap.exists() ? { id: eventId, ...eventSnap.val() } : null;
    }
    if (!event) throw new Error("Evento não encontrado.");

    if (event?.status === "finished")
      throw new Error("Este evento já foi finalizado.");

    if (event?.status === "closed")
      throw new Error("Inscrições encerradas.");

    // ---------------------------------------------------------
    // ⏱️ PAGAMENTO
    // ---------------------------------------------------------
    const paymentDeadlineHours = event?.paymentDeadlineHours || 48;
    const paymentDeadline =
      now + paymentDeadlineHours * 60 * 60 * 1000;

    // ---------------------------------------------------------
    // 📆 PARTICIPAÇÃO
    // ---------------------------------------------------------
    const {
      selectedDays = null,
      daysBreakdown = null,
      useFullEvent: useFullEventFromUI = null,

      // 🏨 alojamento
      lodging: lodgingSelected = null,
      globalLodgingSelected = null,
      dayLodgingChoice = null,
      globalMealSelected = null,
      dayMealChoice = null,
      dayWalkSlotChoice = null,

      // 🚐 transfer
      includeTransfer = false,
      transferPrice = 0,
      transferPoint = null,

      // 💰 total FINAL
      finalPrice = null,
      basePrice = null,
      couponEligibleBase = null,
      discountAmount = 0,
      appliedCoupon = null,
      appliedCoupons = null,

      // 📧 SNAPSHOT PARA EMAIL (🔥 CRÍTICO)
      emailSnapshot = null,

      // 👥 multi-inscrição
      invitees = null,
      paymentMode = null,
      paymentMethod = "stripe",
      useWalletBalance = false,
      walletChargeAmount = null,
      guideRequestId = null,
      editExisting = false,
    } = options || {};

    const daysSelected =
      Array.isArray(selectedDays) && selectedDays.length
        ? selectedDays.map((d) => String(d))
        : null;

    let isFullEvent = true;
    const totalDays = Array.isArray(event?.meetingPoints)
      ? event.meetingPoints.length
      : null;

    if (typeof useFullEventFromUI === "boolean") {
      isFullEvent = useFullEventFromUI;
    } else if (totalDays && daysSelected && daysSelected.length < totalDays) {
      isFullEvent = false;
    }

    const dayWalkSlotChoiceNormalized = {};
    if (dayWalkSlotChoice && typeof dayWalkSlotChoice === "object") {
      const allowedDays = new Set(
        isFullEvent
          ? Array.from({ length: totalDays || 0 }, (_, i) => String(i))
          : Array.isArray(daysSelected)
            ? daysSelected.map((d) => String(d))
            : []
      );
      Object.entries(dayWalkSlotChoice).forEach(([dayIndexRaw, slotIdRaw]) => {
        const dayIndex = String(dayIndexRaw);
        const slotId = String(slotIdRaw || "").trim();
        if (!slotId) return;
        if (allowedDays.size > 0 && !allowedDays.has(dayIndex)) return;
        dayWalkSlotChoiceNormalized[dayIndex] = slotId;
      });
    }

    const participation = {
      isFullEvent,
      daysSelected,
      daysBreakdown: Array.isArray(daysBreakdown)
        ? daysBreakdown
        : null,
      dayWalkSlotChoice:
        Object.keys(dayWalkSlotChoiceNormalized).length > 0
          ? dayWalkSlotChoiceNormalized
          : null,
    };

    // ---------------------------------------------------------
    // 🏨 ALOJAMENTO (NORMALIZADO)
    // ---------------------------------------------------------
    let lodging = lodgingSelected && typeof lodgingSelected === "object"
      ? lodgingSelected
      : null;

    if (!lodging && globalLodgingSelected) {
      lodging = {
        mode: "global",
        global: {
          id: globalLodgingSelected.id || null,
          name: globalLodgingSelected.name || null,
          type: globalLodgingSelected.type || null,
          price:
            typeof globalLodgingSelected.price === "number"
              ? globalLodgingSelected.price
              : 0,
        },
      };
    } else if (!lodging && dayLodgingChoice && typeof dayLodgingChoice === "object") {
      lodging = {
        mode: "byDay",
        byDay: dayLodgingChoice, // { dayIndex: lodgingId }
      };
    }

    let meals = null;

    const selectedGlobalMeals = Array.isArray(globalMealSelected)
      ? globalMealSelected.filter(Boolean)
      : globalMealSelected
        ? [globalMealSelected]
        : [];

    if (selectedGlobalMeals.length) {
      meals = {
        mode: "global",
        global: selectedGlobalMeals.map((meal) => ({
          id: meal.id || null,
          name: meal.name || null,
          menu: meal.menu || null,
          price: typeof meal.price === "number" ? meal.price : 0,
        })),
      };
    } else if (dayMealChoice && typeof dayMealChoice === "object") {
      meals = {
        mode: "byDay",
        byDay: dayMealChoice,
      };
    }

    // ---------------------------------------------------------
    // 🚐 TRANSFER (SEPARADO DO PAGAMENTO)
    // ---------------------------------------------------------
    const transfer = {
      enabled: !!includeTransfer,
      price: includeTransfer
        ? Number(transferPrice) || 0
        : 0,
      point:
        includeTransfer && transferPoint && typeof transferPoint === "object"
          ? {
              id: transferPoint.id || null,
              name: transferPoint.name || null,
              date: transferPoint.date || null,
              time: transferPoint.time || null,
              location:
                transferPoint.location &&
                typeof transferPoint.location === "object"
                  ? {
                      lat:
                        typeof transferPoint.location.lat === "number"
                          ? transferPoint.location.lat
                          : null,
                      lon:
                        typeof transferPoint.location.lon === "number"
                          ? transferPoint.location.lon
                          : null,
                      placeName: transferPoint.location.placeName || null,
                    }
                  : null,
            }
          : null,
    };

    // ---------------------------------------------------------
    // 💰 PREÇO FINAL (SEM RECONTAS NO UI)
    // ---------------------------------------------------------
    const eventAllowsCoupons = event?.allowCoupons === true;

    const rawBasePrice =
      typeof basePrice === "number"
        ? basePrice
        : typeof finalPrice === "number"
          ? finalPrice
          : Number(event?.price || 0);
    const safeBasePrice = Number.isFinite(rawBasePrice) ? rawBasePrice : 0;
    const requestedDiscountAmount =
      !adminTargetUid && eventAllowsCoupons &&
      Number.isFinite(Number(discountAmount)) &&
      Number(discountAmount) > 0
        ? Number(discountAmount)
        : 0;
    const eligibleCouponBase = Number.isFinite(Number(couponEligibleBase))
      ? Math.max(0, Number(couponEligibleBase))
      : isFullEvent
        ? Math.max(0, Number(event?.price || 0))
        : Math.max(
            0,
            (Array.isArray(daysSelected) ? daysSelected : []).reduce(
              (sum, dayIndex) =>
                sum + Number(event?.meetingPoints?.[Number(dayIndex)]?.dayPrice || 0),
              0
            )
          );
    const requestedCouponList = Array.isArray(appliedCoupons) && appliedCoupons.length > 0
      ? appliedCoupons
      : appliedCoupon && typeof appliedCoupon === "object"
        ? [appliedCoupon]
        : [];
    const compensationCoupons = requestedCouponList.filter((coupon) =>
      coupon?.isCompensation === true ||
      String(coupon?.source || "").toLowerCase() === "event_manage_cancellation" ||
      String(coupon?.title || "").trim().toLowerCase() === "vale de compensação"
    );
    if (compensationCoupons.length > 1 || (compensationCoupons.length === 1 && requestedCouponList.length > 1)) {
      throw new Error("Só podes usar um vale de compensação de cada vez e sem combinar com outros cupões.");
    }
    const maxCouponDiscount = compensationCoupons.length === 1
      ? safeBasePrice
      : Math.round(eligibleCouponBase * 25) / 100;
    const safeDiscountAmount = Math.min(requestedDiscountAmount, maxCouponDiscount);
    // O total é sempre recalculado a partir dos dados validados. Não confiar no
    // finalPrice recebido do cliente, que pode estar desatualizado/manipulado.
    const rawPayablePrice = Math.max(0, safeBasePrice - safeDiscountAmount);
    const safePayablePriceBeforeWallet = eventAllowsCoupons
      ? Number.isFinite(rawPayablePrice)
        ? rawPayablePrice
        : 0
      : safeBasePrice;

    const normalizedCoupons = !adminTargetUid && eventAllowsCoupons
      ? requestedCouponList.filter((coupon) => coupon && typeof coupon === "object" && coupon.rewardId).map((coupon) => ({
            rewardId: String(coupon.rewardId).trim(),
            title: coupon.title || "Cupão",
            couponType:
              coupon.couponType === "discount_pct"
                ? "discount_pct"
                : "value_eur",
            couponValue: Number(coupon.couponValue) || 0,
            discountAmount: Number(coupon.discountAmount) || 0,
            claimedAt: Number(coupon.claimedAt) || null,
            source: coupon.source || null,
            isCompensation: coupon.isCompensation === true,
            appliedAt: now,
        }))
      : [];
    const normalizedCoupon = normalizedCoupons[0] || null;

    for (const coupon of normalizedCoupons) {
      const mirrorPath =
        `users/${uid}/gamification/rewardsClaimed/${coupon.rewardId}`;
      const canonicalPath =
        `gamification/users/${uid}/rewardsClaimed/${coupon.rewardId}`;
      const [mirrorSnap, canonicalSnap] = await Promise.all([
        getDb(ref(db, mirrorPath)),
        getDb(ref(db, canonicalPath)),
      ]);
      const freshClaim = mirrorSnap.exists()
        ? mirrorSnap.val() || {}
        : canonicalSnap.exists() ? canonicalSnap.val() || {} : null;

      logEvent("event_coupon", "Validação de vale na inscrição", {
        uid,
        eventId,
        rewardId: coupon.rewardId,
        validationPath: mirrorSnap.exists() ? mirrorPath : canonicalPath,
        exists: !!freshClaim,
        status: freshClaim?.status || null,
        usedAt: freshClaim?.usedAt || null,
        usedForEventId: freshClaim?.usedForEventId || null,
      });

      if (!freshClaim || isCouponUsed(freshClaim)) {
        throw new Error("Este vale já foi usado ou deixou de estar disponível.");
      }
    }

    const requestedWalletOnly = !adminTargetUid && String(paymentMethod || "").toLowerCase() === "wallet";
    const wantsWalletDeduction = !adminTargetUid && (useWalletBalance === true || requestedWalletOnly);
    const walletTotalAmount = adminTargetUid ? 0 : Number.isFinite(Number(walletChargeAmount))
      ? Math.max(0, Math.min(Number(walletChargeAmount), safePayablePriceBeforeWallet))
      : requestedWalletOnly
        ? safePayablePriceBeforeWallet
        : 0;
    const walletChargeCents = Math.max(0, Math.round(walletTotalAmount * 100));
    const payableAfterWallet = Math.max(
      0,
      Math.round((safePayablePriceBeforeWallet - walletTotalAmount) * 100) / 100
    );
    const wantsWalletOnlyPayment = requestedWalletOnly || payableAfterWallet <= 0;
    const adminFreeRegistration = !!adminTargetUid && payableAfterWallet <= 0;

    const pricing = {
      baseTotal: safeBasePrice,
      discountTotal: safeDiscountAmount,
      walletTotal: walletTotalAmount,
      total: payableAfterWallet,
      payableTotal: payableAfterWallet,
      totalBeforeWallet: safePayablePriceBeforeWallet,
    };

    let walletTxId = wantsWalletDeduction
      ? `event-wallet-${eventId}-${uid}-${now}`
      : null;
    let walletBalanceAfterCents = null;
    let walletWasDebited = false;

    const payment = adminFreeRegistration
      ? {
          provider: "admin",
          status: "paid",
          paidAt: now,
          amountTotal: 0,
          currency: "eur",
        }
      : wantsWalletOnlyPayment
      ? {
          provider: "wallet",
          status: "paid",
          paidAt: now,
          amountTotal: safePayablePriceBeforeWallet,
          currency: "eur",
          walletTxId: walletTxId || null,
        }
      : {
          provider: "stripe",
          status: "pending",
          amountTotal: payableAfterWallet,
          walletAppliedTotal: walletTotalAmount > 0 ? walletTotalAmount : null,
          currency: "eur",
          deadline: paymentDeadline ?? null,
        };

    let referral = null;
    try {
      const pendingInviteSnap = await getDb(ref(db, `users/${uid}/pendingEventInvites/${eventId}`));
      const pendingInvite = pendingInviteSnap.exists() ? pendingInviteSnap.val() || {} : null;
      const claimedLocal = adminTargetUid ? null : getClaimedEventInvite();
      const localMatches =
        claimedLocal &&
        String(claimedLocal.eventId || "") === String(eventId) &&
        String(claimedLocal.inviterUid || "") &&
        String(claimedLocal.inviterUid || "") !== String(uid);

      const source = pendingInvite?.inviterUid ? pendingInvite : localMatches ? claimedLocal : null;
      if (source?.inviterUid && String(source.inviterUid) !== String(uid)) {
        referral = {
          inviteId: String(source.inviteId || ""),
          inviterUid: String(source.inviterUid || ""),
          inviterName: String(source.inviterName || "").trim(),
          eventId: String(eventId),
          eventName: String(event?.name || source.eventName || "").trim(),
          claimedAt: Number(source.claimedAt || now) || now,
        };
      }
    } catch (e) {
      console.warn("Falha a carregar referral do evento:", e);
    }

    // ---------------------------------------------------------
    // 📦 REGISTO FINAL (ANTI-REACT-CRASH)
    // ---------------------------------------------------------
    const registrationStatus = wantsWalletOnlyPayment ? "confirmed" : "pending";
    const registration = {
      uid,
      status: registrationStatus,
      registeredAt: now,
      confirmedAt: wantsWalletOnlyPayment ? now : null,
      registeredByAdminUid: adminTargetUid ? actor.uid : null,

      name:
        user.fullName ||
        user.displayName ||
        user.name ||
        user.email?.split("@")[0] ||
        uid,

      email: user.email || null,
      phoneNumber:
        user.phoneNumber ||
        user.phone ||
        user.mobile ||
        null,

      ccNumber,
      dob: birthdate,

      payment,
      participation,
      lodging,
      meals,
      transfer,
      transferPoint: transfer?.point || null,
      pricing,
      coupon: normalizedCoupon,
      coupons: normalizedCoupons,

      // 🔥 ESTE CAMPO É O QUE O SERVER PRECISA
      emailSnapshot: emailSnapshot || null,

      // 👥 multi-inscrição
      invitees: invitees && typeof invitees === "object" ? invitees : null,
      inviteesCount: invitees && typeof invitees === "object" ? Object.keys(invitees).length : 0,
      paymentMode: paymentMode || null,
      referral,

      // legacy
      paymentMethod: adminFreeRegistration ? "admin" : wantsWalletOnlyPayment ? "wallet" : "stripe",
      paymentProvider: adminFreeRegistration ? "admin" : wantsWalletOnlyPayment ? "wallet" : "stripe",
      paymentDeadline: payment?.deadline ?? null,
      isFullEvent,
      daysSelected,
      participated: false,
      trophy: false,
      guideRequestId: guideRequestId ? String(guideRequestId) : null,
    };

    if (
      wantsWalletOnlyPayment &&
      !adminTargetUid &&
      paymentMode === "single" &&
      invitees &&
      typeof invitees === "object"
    ) {
      const inviteesCopy = { ...invitees };
      Object.keys(inviteesCopy).forEach((inviteeId) => {
        const inv = inviteesCopy[inviteeId];
        if (!inv || typeof inv !== "object") return;
        const inviteeAmount =
          Number(inv?.pricing?.payableTotal ?? inv?.pricing?.total ?? inv?.finalPrice ?? 0) || 0;
        inviteesCopy[inviteeId] = {
          ...inv,
          status: inv.status === "registered" ? "registered" : "paid",
          payment: {
            provider: "wallet",
            status: "paid",
            paidAt: now,
            amountTotal: inviteeAmount,
            currency: "eur",
            walletTxId: walletTxId || null,
          },
        };
      });
      registration.invitees = inviteesCopy;
      registration.inviteesCount = Object.keys(inviteesCopy).length;
    }

    const meetingPoints = Array.isArray(event?.meetingPoints) ? event.meetingPoints : [];
    const slotCapacityByKey = {};
    meetingPoints.forEach((day, dayIndex) => {
      const slots = Array.isArray(day?.walkSlots) ? day.walkSlots : [];
      slots.forEach((slot, slotIndex) => {
        const slotId = String(slot?.id || `slot-${dayIndex + 1}-${slotIndex + 1}`);
        const capRaw =
          slot?.maxParticipants != null && slot?.maxParticipants !== ""
            ? Number(slot.maxParticipants)
            : null;
        slotCapacityByKey[`${dayIndex}|${slotId}`] = {
          dayIndex,
          slotId,
          capacity:
            capRaw != null && Number.isFinite(capRaw)
              ? Math.max(0, Math.floor(capRaw))
              : null,
          time: String(slot?.time || "").trim() || null,
        };
      });
    });

    const collectAssignmentsFromParticipation = (part) => {
      if (!part || typeof part !== "object") return [];
      const out = [];
      const daysSelectedLocal =
        part.isFullEvent === true
          ? meetingPoints.map((_, idx) => idx)
          : Array.isArray(part.daysSelected)
            ? part.daysSelected
                .map((v) => Number(v))
                .filter((n) => Number.isInteger(n) && n >= 0)
            : [];
      const dayChoice =
        part.dayWalkSlotChoice && typeof part.dayWalkSlotChoice === "object"
          ? part.dayWalkSlotChoice
          : {};
      const breakdown = Array.isArray(part.daysBreakdown) ? part.daysBreakdown : [];
      const byDay = {};

      breakdown.forEach((row) => {
        const dayIndex = Number(row?.dayIndex);
        if (!Number.isInteger(dayIndex) || dayIndex < 0) return;
        const slotIdRaw = row?.walkSlotId ?? dayChoice[dayIndex] ?? dayChoice[String(dayIndex)];
        const slotId = String(slotIdRaw || "").trim();
        if (!slotId) return;
        byDay[dayIndex] = slotId;
      });

      Object.entries(dayChoice).forEach(([dayIndexRaw, slotIdRaw]) => {
        const dayIndex = Number(dayIndexRaw);
        const slotId = String(slotIdRaw || "").trim();
        if (!Number.isInteger(dayIndex) || dayIndex < 0 || !slotId) return;
        if (!byDay[dayIndex]) byDay[dayIndex] = slotId;
      });

      daysSelectedLocal.forEach((dayIndex) => {
        const slotId = byDay[dayIndex];
        if (!slotId) return;
        out.push({ dayIndex, slotId, key: `${dayIndex}|${slotId}` });
      });

      return out;
    };

    const validateRequiredSlots = (part, participantLabel) => {
      if (!part || typeof part !== "object") return;
      const selectedDays =
        part.isFullEvent === true
          ? meetingPoints.map((_, idx) => idx)
          : Array.isArray(part.daysSelected)
            ? part.daysSelected
                .map((v) => Number(v))
                .filter((n) => Number.isInteger(n) && n >= 0)
            : [];
      if (!selectedDays.length) return;

      const chosenByDay = {};
      collectAssignmentsFromParticipation(part).forEach((entry) => {
        chosenByDay[entry.dayIndex] = entry.slotId;
      });

      const missingDays = selectedDays.filter((dayIndex) => {
        const slots = Array.isArray(meetingPoints[dayIndex]?.walkSlots)
          ? meetingPoints[dayIndex].walkSlots
          : [];
        if (!slots.length) return false;
        return !chosenByDay[dayIndex];
      });

      if (missingDays.length > 0) {
        throw new Error(
          `${participantLabel}: falta selecionar horário de caminhada nos dias ${missingDays
            .map((d) => d + 1)
            .join(", ")}.`
        );
      }
    };

    validateRequiredSlots(participation, "Titular");
    if (invitees && typeof invitees === "object") {
      Object.values(invitees).forEach((inv, idx) => {
        const label =
          String(inv?.name || "").trim() ||
          String(inv?.email || "").trim() ||
          `Convidado ${idx + 1}`;
        validateRequiredSlots(inv?.participation || null, label);
      });
    }

    const existingRegsSnap = await getDb(ref(db, `eventRegistrations/${eventId}`));
    const existingRegs = existingRegsSnap.exists() ? existingRegsSnap.val() || {} : {};
    const existingRegistration = existingRegs?.[uid] && typeof existingRegs[uid] === "object"
      ? existingRegs[uid]
      : null;
    if (editExisting && !existingRegistration) {
      throw new Error("Inscrição não encontrada.");
    }
    if (adminTargetUid && !editExisting) {
      const existingStatus = String(existingRegs?.[uid]?.status || "").toLowerCase();
      if (existingRegs?.[uid] && !["cancelled", "canceled", "rejected", "refunded", "declined", "removed"].includes(existingStatus)) {
        throw new Error("Este utilizador já tem uma inscrição ativa neste evento.");
      }
    }

    const transferCapacityRaw = Number(event?.transferSeats);
    if (Number.isFinite(transferCapacityRaw) && transferCapacityRaw >= 0) {
      const registrationsWithoutCurrentUser = { ...existingRegs };
      delete registrationsWithoutCurrentUser[uid];
      const alreadyReserved = countReservedTransferSeats(registrationsWithoutCurrentUser);
      const requestedTransferSeats =
        (transfer.enabled ? 1 : 0) +
        Object.values(invitees && typeof invitees === "object" ? invitees : {}).reduce(
          (total, invitee) => total + (invitee?.transfer?.enabled === true ? 1 : 0),
          0
        );
      const transferCapacity = Math.max(0, Math.floor(transferCapacityRaw));
      if (
        requestedTransferSeats > 0 &&
        alreadyReserved + requestedTransferSeats > transferCapacity
      ) {
        throw new Error("Já não existem lugares de transfer suficientes para esta inscrição.");
      }
    }
    const occupancyByKey = {};

    const shouldCountRegistration = (reg) => {
      const status = String(reg?.status || "").toLowerCase();
      return !["cancelled", "canceled", "rejected", "declined", "removed"].includes(status);
    };

    Object.entries(existingRegs).forEach(([regUid, reg]) => {
      if (!reg || typeof reg !== "object") return;
      if (regUid === uid) return;
      if (!shouldCountRegistration(reg)) return;

      collectAssignmentsFromParticipation(reg.participation).forEach((entry) => {
        if (!slotCapacityByKey[entry.key]) return;
        occupancyByKey[entry.key] = (occupancyByKey[entry.key] || 0) + 1;
      });

      const invs = reg.invitees && typeof reg.invitees === "object" ? reg.invitees : {};
      Object.values(invs).forEach((inv) => {
        if (!inv || typeof inv !== "object") return;
        const invStatus = String(inv.status || "").toLowerCase();
        if (["cancelled", "canceled", "rejected", "declined", "removed"].includes(invStatus)) {
          return;
        }
        collectAssignmentsFromParticipation(inv.participation).forEach((entry) => {
          if (!slotCapacityByKey[entry.key]) return;
          occupancyByKey[entry.key] = (occupancyByKey[entry.key] || 0) + 1;
        });
      });
    });

    const pendingAddsByKey = {};
    const registerAssignment = (entry, participantLabel) => {
      const slotMeta = slotCapacityByKey[entry.key];
      if (!slotMeta) return;
      if (!Number.isFinite(slotMeta.capacity)) return;
      const current = occupancyByKey[entry.key] || 0;
      const pending = pendingAddsByKey[entry.key] || 0;
      const next = current + pending + 1;
      if (next > slotMeta.capacity) {
        const day = slotMeta.dayIndex + 1;
        const timeLabel = slotMeta.time ? ` (${slotMeta.time})` : "";
        throw new Error(
          `${participantLabel}: lotação esgotada para o Dia ${day}${timeLabel}.`
        );
      }
      pendingAddsByKey[entry.key] = pending + 1;
    };

    collectAssignmentsFromParticipation(participation).forEach((entry) =>
      registerAssignment(entry, "Titular")
    );
    if (invitees && typeof invitees === "object") {
      Object.values(invitees).forEach((inv, idx) => {
        const label =
          String(inv?.name || "").trim() ||
          String(inv?.email || "").trim() ||
          `Convidado ${idx + 1}`;
        collectAssignmentsFromParticipation(inv?.participation || null).forEach((entry) =>
          registerAssignment(entry, label)
        );
      });
    }

    const existingIsPaid = editExisting && (
      String(existingRegistration?.payment?.status || "").toLowerCase() === "paid" ||
      existingRegistration?.confirmed === true ||
      String(existingRegistration?.status || "").toLowerCase() === "confirmed"
    );

    if (existingIsPaid) {
      const preservePaidCredits = (oldRecord, nextRecord) => {
        if (!nextRecord || typeof nextRecord !== "object") return;
        const base = Math.max(0, Number(nextRecord?.pricing?.baseTotal || 0));
        const discount = Math.min(base, Math.max(0, Number(oldRecord?.pricing?.discountTotal || 0)));
        const beforeWallet = Math.max(0, Math.round((base - discount) * 100) / 100);
        const wallet = Math.min(beforeWallet, Math.max(0, Number(oldRecord?.pricing?.walletTotal || 0)));
        nextRecord.pricing = {
          ...(nextRecord.pricing || {}),
          discountTotal: discount,
          walletTotal: wallet,
          totalBeforeWallet: beforeWallet,
          total: Math.max(0, Math.round((beforeWallet - wallet) * 100) / 100),
          payableTotal: Math.max(0, Math.round((beforeWallet - wallet) * 100) / 100),
        };
        nextRecord.coupon = oldRecord?.coupon || null;
        nextRecord.coupons = oldRecord?.coupons || [];
        if (nextRecord.emailSnapshot) {
          nextRecord.emailSnapshot = {
            ...nextRecord.emailSnapshot,
            total: nextRecord.pricing.total,
            walletAppliedTotal: wallet,
          };
        }
      };
      preservePaidCredits(existingRegistration, registration);
      const oldInvitees = existingRegistration?.invitees && typeof existingRegistration.invitees === "object"
        ? existingRegistration.invitees
        : {};
      const newInvitees = registration?.invitees && typeof registration.invitees === "object"
        ? registration.invitees
        : {};
      Object.entries(newInvitees).forEach(([inviteeId, invitee]) => {
        if (oldInvitees[inviteeId]) preservePaidCredits(oldInvitees[inviteeId], invitee);
      });
    }

    if (editExisting && adminTargetUid && existingIsPaid) {
      const oldInvitees = existingRegistration?.invitees && typeof existingRegistration.invitees === "object"
        ? existingRegistration.invitees
        : {};
      const newInvitees = registration?.invitees && typeof registration.invitees === "object"
        ? registration.invitees
        : {};
      const previousTotal = registrationPaidTotal(existingRegistration) + Object.values(oldInvitees)
        .reduce((sum, invitee) => sum + registrationPaidTotal(invitee), 0);
      const proposedTotal = registrationPaidTotal(registration) + Object.values(newInvitees)
        .reduce((sum, invitee) => sum + registrationPaidTotal(invitee), 0);
      const difference = Math.round((proposedTotal - previousTotal) * 100) / 100;
      const amendmentId = `admin_amend_${now}`;
      const proposed = {
        participation: registration.participation,
        lodging: registration.lodging,
        meals: registration.meals,
        transfer: registration.transfer,
        transferPoint: registration.transferPoint,
        pricing: registration.pricing,
        emailSnapshot: registration.emailSnapshot,
        invitees: registration.invitees,
        inviteesCount: registration.inviteesCount,
        paymentMode: registration.paymentMode,
        isFullEvent: registration.isFullEvent,
        daysSelected: registration.daysSelected,
      };
      const amendment = {
        id: amendmentId,
        status: difference > 0 ? "payment_pending" : "applied",
        createdAt: now,
        updatedAt: now,
        createdByAdminUid: actor.uid,
        amountTotal: Math.abs(difference),
        difference,
        previousTotal,
        proposedTotal,
        proposed,
        payment: difference > 0
          ? { provider: "stripe", status: "pending", amountTotal: difference, currency: "eur" }
          : { provider: "wallet", status: "paid", amountTotal: 0, currency: "eur", paidAt: now },
      };

      if (difference > 0) {
        await update(ref(db), stripUndefinedDeep({
          [`eventRegistrations/${eventId}/${uid}/pendingAmendment`]: amendment,
          [`userEvents/${uid}/${eventId}/pendingAmendment`]: amendment,
        }));
        return { mode: "admin_payment_difference", amendmentId, amountTotal: difference };
      }

      const refundCents = Math.max(0, Math.round(-difference * 100));
      let balanceAfterCents = null;
      if (refundCents > 0) {
        const walletResult = await runTransaction(ref(db, `users/${uid}/wallet`), (current) => {
          const wallet = current && typeof current === "object" ? { ...current } : {};
          wallet.balanceCents = Math.max(0, Number(wallet.balanceCents || 0)) + refundCents;
          wallet.updatedAt = now;
          return wallet;
        });
        if (!walletResult.committed) throw new Error("Não foi possível creditar a diferença na carteira.");
        balanceAfterCents = Number(walletResult.snapshot?.val()?.balanceCents || 0);
      }
      const applied = {
        ...existingRegistration,
        ...proposed,
        payment: existingRegistration.payment,
        status: existingRegistration.status,
        confirmedAt: existingRegistration.confirmedAt || now,
        updatedAt: now,
        pendingAmendment: null,
        amendmentHistory: {
          ...(existingRegistration.amendmentHistory || {}),
          [amendmentId]: amendment,
        },
      };
      const adminUpdates = {
        [`eventRegistrations/${eventId}/${uid}`]: applied,
        [`userEvents/${uid}/${eventId}`]: { ...applied, role: "participant" },
      };
      if (refundCents > 0) {
        const walletTxId = `event-amendment-refund-${eventId}-${now}`;
        adminUpdates[`walletTransactions/${uid}/${walletTxId}`] = {
          id: walletTxId,
          type: "credit",
          source: "event_registration_amendment",
          eventId,
          amendmentId,
          amountCents: refundCents,
          balanceAfterCents,
          createdAt: now,
          createdByAdminUid: actor.uid,
        };
      }
      await update(ref(db), stripUndefinedDeep(adminUpdates));
      return {
        mode: refundCents > 0 ? "admin_wallet_refund" : "admin_amendment_applied",
        amendmentId,
        walletRefundAmount: refundCents / 100,
      };
    }

    if (existingIsPaid) {
      const ownerValidation = validatePaidRegistrationAdditions(existingRegistration, registration);
      const validationErrors = [...ownerValidation.errors];
      const oldInvitees = existingRegistration?.invitees && typeof existingRegistration.invitees === "object"
        ? existingRegistration.invitees
        : {};
      const newInvitees = registration?.invitees && typeof registration.invitees === "object"
        ? registration.invitees
        : {};
      Object.entries(oldInvitees).forEach(([inviteeId, oldInvitee]) => {
        if (!newInvitees[inviteeId]) {
          validationErrors.push("Não podes remover participantes de uma inscrição já paga.");
          return;
        }
        const result = validatePaidRegistrationAdditions(oldInvitee, newInvitees[inviteeId]);
        validationErrors.push(...result.errors.map((message) => `${oldInvitee?.name || oldInvitee?.email || "Convidado"}: ${message}`));
      });
      if (validationErrors.length) {
        throw new Error([...new Set(validationErrors)].join(" "));
      }

      const previousTotal = registrationPaidTotal(existingRegistration) + Object.values(oldInvitees)
        .reduce((sum, invitee) => sum + registrationPaidTotal(invitee), 0);
      const proposedTotal = registrationPaidTotal(registration) + Object.values(newInvitees)
        .reduce((sum, invitee) => sum + registrationPaidTotal(invitee), 0);
      const difference = Math.max(0, Math.round((proposedTotal - previousTotal) * 100) / 100);
      const amendmentId = `amend_${now}`;
      const proposed = {
        participation: registration.participation,
        lodging: registration.lodging,
        meals: registration.meals,
        transfer: registration.transfer,
        transferPoint: registration.transferPoint,
        pricing: registration.pricing,
        emailSnapshot: registration.emailSnapshot,
        invitees: registration.invitees,
        inviteesCount: registration.inviteesCount,
        paymentMode: registration.paymentMode,
        isFullEvent: registration.isFullEvent,
        daysSelected: registration.daysSelected,
      };
      const amendment = {
        id: amendmentId,
        status: difference > 0 ? "payment_pending" : "applied",
        createdAt: now,
        updatedAt: now,
        amountTotal: difference,
        previousTotal,
        proposedTotal,
        proposed,
        payment: difference > 0
          ? { provider: "stripe", status: "pending", amountTotal: difference, currency: "eur" }
          : { provider: "none", status: "paid", amountTotal: 0, currency: "eur", paidAt: now },
      };

      if (difference <= 0) {
        const applied = {
          ...existingRegistration,
          ...proposed,
          payment: existingRegistration.payment,
          status: existingRegistration.status,
          confirmedAt: existingRegistration.confirmedAt || now,
          updatedAt: now,
          amendmentHistory: {
            ...(existingRegistration.amendmentHistory || {}),
            [amendmentId]: amendment,
          },
          pendingAmendment: null,
        };
        await update(ref(db), stripUndefinedDeep({
          [`eventRegistrations/${eventId}/${uid}`]: applied,
          [`userEvents/${uid}/${eventId}`]: { ...applied, role: "participant" },
        }));
        return { mode: "paid_amendment_applied", amendmentId, amountTotal: 0 };
      }

      await update(ref(db), stripUndefinedDeep({
        [`eventRegistrations/${eventId}/${uid}/pendingAmendment`]: amendment,
        [`userEvents/${uid}/${eventId}/pendingAmendment`]: amendment,
      }));
      const apiBase = String(import.meta.env.VITE_API_BASE || "").replace(/\/$/, "");
      return {
        mode: "paid_amendment",
        amendmentId,
        amountTotal: difference,
        paymentUrl: `${apiBase}/pagar/evento/${encodeURIComponent(eventId)}/${encodeURIComponent(uid)}?amendmentId=${encodeURIComponent(amendmentId)}`,
      };
    }

    if (editExisting && existingRegistration) {
      const oldDiscount = Math.max(0, Number(existingRegistration?.pricing?.discountTotal || 0));
      const oldWallet = Math.max(0, Number(existingRegistration?.pricing?.walletTotal || 0));
      const nextDiscount = Math.min(oldDiscount, safeBasePrice);
      const afterDiscount = Math.max(0, Math.round((safeBasePrice - nextDiscount) * 100) / 100);
      const nextWallet = Math.min(oldWallet, afterDiscount);
      const walletRefundCents = Math.max(0, Math.round((oldWallet - nextWallet) * 100));
      let walletBalanceAfterCents = null;
      if (walletRefundCents > 0) {
        const walletResult = await runTransaction(ref(db, `users/${uid}/wallet`), (current) => {
          const wallet = current && typeof current === "object" ? { ...current } : {};
          wallet.balanceCents = Math.max(0, Number(wallet.balanceCents || 0)) + walletRefundCents;
          wallet.updatedAt = now;
          return wallet;
        });
        if (!walletResult.committed) throw new Error("Não foi possível ajustar o saldo da carteira.");
        walletBalanceAfterCents = Number(walletResult.snapshot?.val()?.balanceCents || 0);
      }
      const payable = Math.max(0, Math.round((afterDiscount - nextWallet) * 100) / 100);
      registration.registeredAt = existingRegistration.registeredAt || now;
      registration.referral = existingRegistration.referral || registration.referral;
      registration.coupon = existingRegistration.coupon || null;
      registration.coupons = existingRegistration.coupons || [];
      registration.pricing = {
        ...registration.pricing,
        discountTotal: nextDiscount,
        walletTotal: nextWallet,
        totalBeforeWallet: afterDiscount,
        total: payable,
        payableTotal: payable,
      };
      registration.payment = payable > 0
        ? {
            provider: "stripe",
            status: "pending",
            amountTotal: payable,
            walletAppliedTotal: nextWallet || null,
            currency: "eur",
            deadline: paymentDeadline ?? null,
          }
        : {
            provider: nextWallet > 0 ? "wallet" : "none",
            status: "paid",
            paidAt: now,
            amountTotal: afterDiscount,
            currency: "eur",
          };
      registration.status = payable > 0 ? "pending" : "confirmed";
      registration.confirmedAt = payable > 0 ? null : now;
      registration.paymentMethod = payable > 0 ? "stripe" : (nextWallet > 0 ? "wallet" : "none");
      registration.paymentProvider = registration.paymentMethod;
      registration.paymentDeadline = registration.payment?.deadline ?? null;
      registration.emailSent = false;
      registration.processing = false;
      registration.updatedAt = now;
      if (registration.emailSnapshot) {
        registration.emailSnapshot = {
          ...registration.emailSnapshot,
          total: payable,
          walletAppliedTotal: nextWallet,
        };
      }
      const editUpdates = {
        [`eventRegistrations/${eventId}/${uid}`]: registration,
        [`userEvents/${uid}/${eventId}`]: { ...registration, role: "participant" },
      };
      if (walletRefundCents > 0) {
        const walletTxId = `event-edit-refund-${eventId}-${now}`;
        editUpdates[`walletTransactions/${uid}/${walletTxId}`] = {
          id: walletTxId,
          type: "credit",
          source: "event_registration_edit",
          eventId,
          amountCents: walletRefundCents,
          balanceAfterCents: walletBalanceAfterCents,
          createdAt: now,
        };
      }
      await update(ref(db), stripUndefinedDeep(editUpdates));
      return { mode: "edited_pending", amountTotal: payable, walletRefundAmount: walletRefundCents / 100 };
    }

    if (wantsWalletDeduction && walletChargeCents > 0) {
      const walletRef = ref(db, `users/${uid}/wallet`);
      const tx = await runTransaction(walletRef, (current) => {
        const node =
          current && typeof current === "object" ? { ...current } : {};
        const currentCents = Number(node.balanceCents || 0);
        if (!Number.isFinite(currentCents) || currentCents < walletChargeCents) {
          return;
        }
        node.balanceCents = currentCents - walletChargeCents;
        node.updatedAt = now;
        return node;
      });

      if (!tx.committed) {
        throw new Error("Saldo insuficiente para concluir a inscrição.");
      }

      walletWasDebited = true;
      const walletNode = tx.snapshot?.val() || {};
      walletBalanceAfterCents = Number(walletNode.balanceCents || 0);
    }

    const updates = stripUndefinedDeep({
      [`eventRegistrations/${eventId}/${uid}`]: registration,
      [`userEvents/${uid}/${eventId}`]: { ...registration, role: "participant" },
    });

    if (wantsWalletDeduction && walletChargeCents > 0 && walletTxId) {
      updates[`walletTransactions/${uid}/${walletTxId}`] = {
        id: walletTxId,
        type: "debit",
        source: "event_registration",
        eventId,
        amountCents: -walletChargeCents,
        balanceAfterCents:
          walletBalanceAfterCents != null ? walletBalanceAfterCents : null,
        createdAt: now,
      };
    }

    normalizedCoupons.forEach((coupon) => {
      const rewardPaths = [
        `gamification/users/${uid}/rewardsClaimed/${coupon.rewardId}`,
        `users/${uid}/gamification/rewardsClaimed/${coupon.rewardId}`,
      ];
      const couponType = String(coupon.couponType || "value_eur").toLowerCase();
      const couponValue = Number(coupon.couponValue || 0);
      const usedDiscount = Number(coupon.discountAmount || 0);
      const remainingValue =
        couponType === "value_eur"
          ? Math.max(0, Math.round((couponValue - usedDiscount) * 100) / 100)
          : 0;
      const hasRemainder = couponType === "value_eur" && remainingValue > 0;

      rewardPaths.forEach((rewardPath) => {
        updates[`${rewardPath}/usedDiscountAmount`] = usedDiscount;
        updates[`${rewardPath}/lastUsedAt`] = now;
        updates[`${rewardPath}/lastUsedForEventId`] = eventId;

        if (hasRemainder) {
          updates[`${rewardPath}/status`] = "partial";
          updates[`${rewardPath}/couponValue`] = remainingValue;
          updates[`${rewardPath}/usedAt`] = null;
          updates[`${rewardPath}/usedForEventId`] = null;
        } else {
          updates[`${rewardPath}/status`] = "used";
          updates[`${rewardPath}/usedAt`] = now;
          updates[`${rewardPath}/usedForEventId`] = eventId;
        }
      });
    });

    const sanitizedUpdates = stripUndefinedDeep(updates);
    const undefinedPaths = findUndefinedPaths(sanitizedUpdates);
    if (undefinedPaths.length > 0) {
      console.warn("⚠️ [EVENT_REGISTER] undefined paths before update:", undefinedPaths.slice(0, 20));
    }

    try {
      try {
        await update(ref(db), sanitizedUpdates);
      } catch (primaryErr) {
        const permissionDenied =
          String(primaryErr?.code || "").toLowerCase().includes("permission") ||
          String(primaryErr?.message || "").toLowerCase().includes("permission");
        if (permissionDenied && normalizedCoupon?.rewardId) {
          const canonicalPrefix =
            `gamification/users/${uid}/rewardsClaimed/${normalizedCoupon.rewardId}`;
          const permittedUpdates = Object.fromEntries(
            Object.entries(sanitizedUpdates).filter(
              ([path]) => !path.startsWith(canonicalPrefix)
            )
          );
          console.warn(
            "⚠️ [EVENT_REGISTER] canonical coupon write denied; retrying with private mirror",
            { rewardId: normalizedCoupon.rewardId }
          );
          await update(ref(db), permittedUpdates);
        } else {
          console.warn("⚠️ [EVENT_REGISTER] primary update failed, trying JSON fallback:", primaryErr);
          const jsonFallback = JSON.parse(JSON.stringify(sanitizedUpdates));
          await update(ref(db), jsonFallback);
        }
      }
    } catch (writeErr) {
      if (walletWasDebited && walletChargeCents > 0) {
        try {
          await runTransaction(ref(db, `users/${uid}/wallet`), (current) => {
            const node =
              current && typeof current === "object" ? { ...current } : {};
            const currentCents = Number(node.balanceCents || 0);
            node.balanceCents =
              (Number.isFinite(currentCents) ? currentCents : 0) + walletChargeCents;
            node.updatedAt = Date.now();
            return node;
          });
        } catch {}
      }
      throw writeErr;
    }
    if (referral?.eventId && !adminTargetUid) {
      clearClaimedEventInvite(eventId);
    }

    logEvent("events_register", "User registado no evento", {
      eventId,
      uid,
      paymentMethod: payment.provider,
      isFullEvent,
      daysSelected,
      hasLodging: !!lodging,
      hasMeals: !!meals,
      hasTransfer: transfer.enabled,
      discountTotal: pricing.discountTotal,
      total: pricing.total,
      totalBeforeWallet: pricing.totalBeforeWallet,
      walletTotal: pricing.walletTotal,
      referralInviterUid: referral?.inviterUid || null,
    });
    return { mode: "created", amountTotal: pricing.total };
  },

  // -----------------------------------------------------------
  // CONFIRMAR PAGAMENTO + ADICIONAR AO GRUPO
  // -----------------------------------------------------------
  confirmRegistration: async (eventId, targetUid) => {
    if (!eventId || !targetUid) return;

    const adminUid = useAuthStore.getState().user?.uid || null;
    const now = Date.now();

    const eventsState = get().events || [];
    const event = eventsState.find((e) => e.id === eventId) || null;
    const groupId = event?.groupId || null;
    const regSnap = await getDb(ref(db, `eventRegistrations/${eventId}/${targetUid}`));
    const reg = regSnap.exists() ? regSnap.val() || {} : {};
    const walletAppliedCents = Math.max(
      0,
      Math.round(Number(reg?.payment?.walletAppliedTotal || reg?.pricing?.walletTotal || 0) * 100)
    );
    const walletAlreadyDebited =
      !!reg?.payment?.walletDebitedAt || !!reg?.payment?.walletDebitTxId;
    let walletDebitTxId = null;
    let walletBalanceAfterCents = null;

    if (walletAppliedCents > 0 && !walletAlreadyDebited) {
      walletDebitTxId = `event-wallet-${eventId}-${targetUid}-${now}`;
      const walletRef = ref(db, `users/${targetUid}/wallet`);
      const tx = await runTransaction(walletRef, (current) => {
        const node =
          current && typeof current === "object" ? { ...current } : {};
        const currentCents = Number(node.balanceCents || 0);
        if (!Number.isFinite(currentCents) || currentCents < walletAppliedCents) {
          return;
        }
        node.balanceCents = currentCents - walletAppliedCents;
        node.updatedAt = now;
        return node;
      });

      if (!tx.committed) {
        throw new Error("Saldo insuficiente para confirmar a inscrição.");
      }

      const walletNode = tx.snapshot?.val() || {};
      walletBalanceAfterCents = Number(walletNode.balanceCents || 0);
    }

    const updates = {
      [`eventRegistrations/${eventId}/${targetUid}/status`]: "confirmed",
      [`eventRegistrations/${eventId}/${targetUid}/confirmedAt`]: now,
      [`eventRegistrations/${eventId}/${targetUid}/payment/status`]: "paid",
      [`eventRegistrations/${eventId}/${targetUid}/payment/paidAt`]: now,

      [`userEvents/${targetUid}/${eventId}/status`]: "confirmed",
      [`userEvents/${targetUid}/${eventId}/confirmedAt`]: now,
      [`userEvents/${targetUid}/${eventId}/payment/status`]: "paid",
      [`userEvents/${targetUid}/${eventId}/payment/paidAt`]: now,
    };

    // A confirmação tem de dar acesso imediato ao grupo usado pelo LIVE.
    if (groupId) {
      updates[`groups/${groupId}/members/${targetUid}`] = true;
    }

    if (walletDebitTxId) {
      updates[`eventRegistrations/${eventId}/${targetUid}/payment/walletDebitedAt`] = now;
      updates[`eventRegistrations/${eventId}/${targetUid}/payment/walletDebitTxId`] =
        walletDebitTxId;
      updates[`userEvents/${targetUid}/${eventId}/payment/walletDebitedAt`] = now;
      updates[`userEvents/${targetUid}/${eventId}/payment/walletDebitTxId`] =
        walletDebitTxId;
      updates[`walletTransactions/${targetUid}/${walletDebitTxId}`] = {
        id: walletDebitTxId,
        type: "debit",
        source: "event_registration_confirm",
        eventId,
        amountCents: -walletAppliedCents,
        balanceAfterCents:
          walletBalanceAfterCents != null ? walletBalanceAfterCents : null,
        createdAt: now,
        confirmedByUid: adminUid,
      };
    }

    try {
      await update(ref(db), updates);
    } catch (err) {
      if (walletDebitTxId && walletAppliedCents > 0) {
        try {
          await runTransaction(ref(db, `users/${targetUid}/wallet`), (current) => {
            const node =
              current && typeof current === "object" ? { ...current } : {};
            const currentCents = Number(node.balanceCents || 0);
            node.balanceCents =
              (Number.isFinite(currentCents) ? currentCents : 0) + walletAppliedCents;
            node.updatedAt = Date.now();
            return node;
          });
        } catch {}
      }
      throw err;
    }

    // v1.7.x: auto-sync também passa por listenEventRegistrations,
    // mas isto mantém o comportamento imediato que já tinhas.
    logEvent("events_confirm_registration", "Inscrição confirmada", {
      eventId,
      targetUid,
      adminUid,
      groupId,
    });
  },

  // -----------------------------------------------------------
  // 🧠 Helper: verificar se evento está em modo LIVE (MODO A, MULTI-DIA)
  // -----------------------------------------------------------
  isEventLive: (eventId) => {
    const state = get();
    const events = state.events || [];
    const event = events.find((e) => e.id === eventId);
    if (!event) return false;

    return event.isLive === true;
  },

  // 🧠 Helper: utilizador pode ver LIVE / trilho deste evento?
  // 🔴 v1.9.0: isLive é gate GLOBAL → sem LIVE ninguém vê trilho no mapa
  canUserSeeEventLive: (eventId) => {
  try {
    const state = get();
    const auth = useAuthStore.getState();
    const user = auth.user;

    if (!user || !user.uid) {
      logEvent("events_view_route_denied", "Sem utilizador", { eventId });
      return false;
    }

    const events = state.events || [];
    let event = events.find((e) => e.id === eventId) || null;

    // fallback via panel
    if (!event) {
      const panels = usePanelsStore.getState();
      const selectedEvent = panels?.selectedEvent;
      if (selectedEvent?.id === eventId) {
        event = selectedEvent;
        logEvent("events_view_route_fallback", "Usado selectedEvent", {
          eventId,
        });
      }
    }

    if (!event) {
      logEvent("events_view_route_warn", "Evento não encontrado (LIVE)", {
        eventId,
      });
      // Não bloquear aqui — LIVE pode ser resolvido via eventsLive/group
    }

    const role = (user.role || "free").toLowerCase();
    const isAdminOrGuide = role === "admin" || role === "guide";
    const isCreator = event?.createdByUid === user.uid;

    // ⚠️ Não negar já aqui quando isLive vem false do índice público/invites:
    // pode estar stale enquanto groups/eventsLive já está LIVE ON.
    const looksLiveOff = !!event && event.isLive !== true;

    // ✅ Admin / Guide / Creator
    if (isAdminOrGuide || isCreator) {
      logLiveRouteAccess("events_view_route", "LIVE permitido (admin/guide/creator)", {
        eventId,
      });
      return true;
    }

    const diagStart = {
      eventId,
      uid: user.uid,
      role,
      eventFound: !!event,
      eventIsLive: event?.isLive ?? null,
      eventGroupId: event?.groupId || null,
    };
    logLiveRouteAccess("info", "events_view_route_diag: início avaliação LIVE", diagStart);
    try {
      console.info("[LIVE_WEB_DIAG_START]", diagStart);
    } catch {}

    // ✅ Membro do grupo do evento (LIVE)
    try {
      const groupId = event?.groupId || null;
      let hasActiveGroupLiveMatch = false;
      if (groupId) {
        const groupsState = useGroupStore.getState()?.groups || [];
        const group = (Array.isArray(groupsState) ? groupsState : []).find(
          (g) =>
            g?.id === groupId ||
            g?.groupId === groupId ||
            String(g?.id || "") === String(groupId) ||
            String(g?.groupId || "") === String(groupId)
        );
        if (group?.members?.[user.uid] === true || !!group?.members?.[user.uid]) {
          hasActiveGroupLiveMatch =
            String(group?.activeEventId || group?.activeEvent || group?.eventId || "") ===
            String(eventId);
          logLiveRouteAccess("events_view_route", "LIVE permitido (membro do grupo)", {
            eventId,
            groupId,
            uid: user.uid,
          });
          try {
            console.info("[LIVE_WEB_ALLOW_GROUP_MEMBER]", {
              eventId,
              uid: user.uid,
              groupId,
            });
          } catch {}
          return true;
        }
      }

      // Fallback robusto: se o grupo atual do user aponta para este eventId e user é membro.
      const groupState = useGroupStore.getState();
      const currentGroup =
        typeof groupState?.currentGroup === "object" ? groupState.currentGroup : null;
      const currentGroupId =
        currentGroup?.id ||
        currentGroup?.groupId ||
        currentGroup?.firebaseId ||
        currentGroup?.key ||
        null;
      const currentActiveEventId =
        currentGroup?.activeEventId || currentGroup?.activeEvent || currentGroup?.eventId || null;

      if (
        currentGroupId &&
        currentActiveEventId &&
        String(currentActiveEventId) === String(eventId)
      ) {
        hasActiveGroupLiveMatch = true;
        const groupsState = groupState?.groups || [];
        const groupMatch =
          (Array.isArray(groupsState) ? groupsState : []).find(
            (g) =>
              g?.id === currentGroupId ||
              g?.groupId === currentGroupId ||
              g?.firebaseId === currentGroupId ||
              g?.key === currentGroupId
          ) || currentGroup;
        if (groupMatch?.members?.[user.uid] === true || !!groupMatch?.members?.[user.uid]) {
          logLiveRouteAccess("events_view_route", "LIVE permitido (fallback currentGroup)", {
            eventId,
            groupId: currentGroupId,
            uid: user.uid,
          });
          try {
            console.info("[LIVE_WEB_ALLOW_CURRENT_GROUP]", {
              eventId,
              uid: user.uid,
              groupId: currentGroupId,
              currentActiveEventId,
            });
          } catch {}
          return true;
        }
      }

      // Se só temos "isLive=false" no objeto do evento mas o grupo ativo aponta para este evento,
      // tratamos como stale e deixamos continuar para validar inscrição.
      if (looksLiveOff && !hasActiveGroupLiveMatch) {
        logLiveRouteAccess("events_view_route_denied", "LIVE OFF (definitivo)", {
          eventId,
          role,
        });
        try {
          console.warn("[LIVE_WEB_DENY_LIVE_OFF]", {
            eventId,
            uid: user.uid,
            role,
            looksLiveOff,
            hasActiveGroupLiveMatch,
            eventGroupId: groupId,
          });
        } catch {}
        return false;
      }
    } catch {}

    // ✅ Regra principal: apenas inscritos CONFIRMADOS
    const regs = state.eventRegistrations?.[eventId] || {};
    const reg = regs?.[user.uid] || null;
    if (reg && typeof reg === "object") {
      const regStatus = String(
        reg?.status || reg?.state || reg?.registrationStatus || ""
      )
        .trim()
        .toLowerCase();
      if (
        regStatus &&
        regStatus !== "cancelled" &&
        regStatus !== "canceled" &&
        regStatus !== "declined" &&
        regStatus !== "rejected" &&
        regStatus !== "removed" &&
        regStatus !== "expired"
      ) {
        logLiveRouteAccess("events_view_route", "LIVE permitido (inscrição ativa)", {
          eventId,
          status: regStatus,
        });
        try {
          console.info("[LIVE_WEB_ALLOW_REG_ACTIVE]", {
            eventId,
            uid: user.uid,
            status: regStatus,
          });
        } catch {}
        return true;
      }
    }
    let status = (reg?.status || "").toLowerCase();
    if (!status) {
      const ue = state.userEvents?.[eventId] || null;
      status = (ue?.status || "").toLowerCase();
    }
    if (
      status === "confirmed" ||
      status === "active" ||
      status === "paid" ||
      status === "registered" ||
      status === "approved"
    ) {
      logLiveRouteAccess("events_view_route", "LIVE permitido (inscrito confirmado)", {
        eventId,
      });
      try {
        console.info("[LIVE_WEB_ALLOW_STATUS]", {
          eventId,
          uid: user.uid,
          status,
        });
      } catch {}
      return true;
    }

    logLiveRouteAccess("events_view_route_denied", "Sem inscrição confirmada", {
      eventId,
      role,
      status: status || null,
    });
    console.warn("[events_view_route_denied]", {
      eventId,
      uid: user.uid,
      role,
      status: status || null,
      looksLiveOff,
      eventGroupId: event?.groupId || null,
    });
    return false;
  } catch (e) {
    console.warn("canUserSeeEventLive falhou:", e);
    logEvent("events_view_route_error", "Exceção", {
      eventId,
      error: e?.message,
    });
    return false;
  }
},

  // -----------------------------------------------------------
  // 🆕 LÓGICA DO TRILHO NO MAPA — MODO A (LIVE + CONFIRMADOS / GUIA)
  // -----------------------------------------------------------
  getEventRouteForMap: (eventId) => {
    try {
      const state = get();
      const auth = useAuthStore.getState();
      const user = auth.user;
      if (!user || !user.uid) {
        logEvent("events_route_map", "Sem utilizador em getEventRouteForMap", {
          eventId,
          reason: "no_user",
        });
        return null;
      }

      const events = state.events || [];
      let event = events.find((e) => e.id === eventId) || null;

      // 🔁 Fallback: tentar selectedEvent do Panels se ainda não estiver no array
      if (!event) {
        const panels = usePanelsStore.getState();
        const selectedEvent = panels?.selectedEvent;
        if (selectedEvent && selectedEvent.id === eventId) {
          event = selectedEvent;
          logEvent(
            "events_route_map_fallback",
            "Evento obtido via selectedEvent em getEventRouteForMap",
            { eventId }
          );
        }
      }

      if (!event) {
        logEvent(
          "events_route_map",
          "Sem evento em getEventRouteForMap (nada desenhado)",
          { eventId, reason: "no_event" }
        );
        return null;
      }

      const roleRaw = user.role || "free";
      const role = typeof roleRaw === "string" ? roleRaw.toLowerCase() : "free";

      const isAdminOrGuide = role === "admin" || role === "guide";
      const isCreator = event.createdByUid === user.uid;

      // ✅ definido antes de qualquer lógica
      const isLive = state.isEventLive(eventId);

      if (!isLive) {
        logEvent(
          "events_route_map",
          "Evento não está LIVE; rota não enviada para o mapa",
          { eventId }
        );
        return null;
      }

      // Primeiro, usar regra oficial
      let canSee = state.canUserSeeEventLive(eventId);

      // Rede de segurança
      if (!canSee && isLive && (isAdminOrGuide || isCreator)) {
        logEvent(
          "events_route_map_safety_net",
          "Forçado canSee=true para admin/guide/creator (LIVE ON)",
          {
            eventId,
            isAdminOrGuide,
            isCreator,
          }
        );
        canSee = true;
      }

      if (!canSee) {
        logEvent(
          "events_route_map",
          "Utilizador não autorizado a ver trilho no mapa",
          {
            eventId,
            role,
          }
        );
        return null;
      }

      const altRoutes =
        event.alternativeRoutes || event.altRoutes || event.alternatives || null;
      const tourRoutes =
        event.tourRoutes || event.tourGPXRoutes || event.tourGPX || event.tours || null;

      const liveRoute = resolveEventDayRoute(event);
      if (!liveRoute.url && !altRoutes && !tourRoutes) {
        logEvent(
          "events_route_map",
          "Evento sem dados de rota em getEventRouteForMap",
          { eventId }
        );
        return null;
      }

      logEvent("events_route_map", "Rota enviada para o mapa", {
        eventId,
        hasAlt: !!altRoutes,
        routeGPXUrl: liveRoute.url,
        dayIndex: liveRoute.dayIndex,
      });

      return {
        routeGPXUrl: liveRoute.url || null,
        routeDayIndex: liveRoute.dayIndex,
        altRoutes,
        tourRoutes,
        event,
      };
    } catch (e) {
      console.warn("getEventRouteForMap falhou:", e);
      logEvent("events_route_map", "Exceção em getEventRouteForMap", {
        eventId,
        error: e?.message,
      });
      return null;
    }
  },

  // -----------------------------------------------------------
  // 🚦 INICIAR / TERMINAR LIVE DO EVENTO (GUIA)
  // -----------------------------------------------------------
  startEventLive: async (eventId) => {
    if (!eventId) throw new Error("Evento inválido.");

    const adminUid = useAuthStore.getState().user?.uid || null;
    const now = Date.now();

    const eventsState = get().events || [];
    const cachedEvent = eventsState.find((e) => e.id === eventId) || null;
    let freshEvent = null;
    try {
      const snap = await getDb(ref(db, `/events/${eventId}`));
      freshEvent = snap.exists() ? snap.val() : null;
    } catch (e) {
      console.warn("startEventLive: falha ao ler evento fresco", e);
    }
    const event = { ...(cachedEvent || {}), ...(freshEvent || {}), id: eventId };
    const groupId = event?.groupId || null;

    const updates = {
      [`events/${eventId}/isLive`]: true,
      [`events/${eventId}/liveStartedAt`]: now,
      [`events/${eventId}/status`]: "open",
    };

    // Não depender de o painel de gestão estar aberto para sincronizar os
    // inscritos. Ao ligar o LIVE, todos os registos ativos entram no grupo.
    if (groupId) {
      try {
        const registrationsSnap = await getDb(ref(db, `/eventRegistrations/${eventId}`));
        const registrations = registrationsSnap.exists()
          ? registrationsSnap.val() || {}
          : {};
        const inactiveStatuses = new Set([
          "cancelled",
          "canceled",
          "deleted",
          "removed",
          "expired",
          "declined",
          "rejected",
          "absent",
        ]);
        Object.entries(registrations).forEach(([uid, registration]) => {
          const status = String(registration?.status || "").trim().toLowerCase();
          if (uid && !inactiveStatuses.has(status)) {
            updates[`groups/${groupId}/members/${uid}`] = true;
          }
        });
      } catch (error) {
        console.warn("startEventLive: falha ao sincronizar inscritos no grupo", error);
      }
    }

    // ✅ LIVE ON → grupo fica com activeEventId
    if (groupId) {
      updates[`groups/${groupId}/activeEventId`] = eventId;
    }

    if (event && Object.keys(event).length > 0) {
      const isInviteOnly = event.inviteOnly === true;
      const isPublicEvent =
        !isInviteOnly &&
        event.published === true &&
        event.isPublic === true;

      const liveRoute = resolveEventDayRoute(event, now);
      const routeGPXUrl = liveRoute.url || null;
      const alternativeRoutes =
        event.alternativeRoutes || event.altRoutes || event.alternatives || null;
      const tourRoutes =
        event.tourRoutes || event.tourGPXRoutes || event.tourGPX || event.tours || null;

      if (groupId) {
        updates[`eventsLive/${eventId}`] = {
          groupId,
          isLive: true,
          status: "open",
          liveStartedAt: now,
          routeGPXUrl: routeGPXUrl || null,
          routeDayIndex: liveRoute.dayIndex,
          alternativeRoutes: alternativeRoutes || null,
          tourRoutes: tourRoutes || null,
          updatedAt: now,
        };
      }

      if (isPublicEvent) {
        updates[`eventsPublic/${eventId}/isLive`] = true;
        updates[`eventsPublic/${eventId}/liveStartedAt`] = now;
        updates[`eventsPublic/${eventId}/status`] = "open";
        if (routeGPXUrl) {
          updates[`eventsPublic/${eventId}/routeGPXUrl`] = routeGPXUrl;
        }
        if (alternativeRoutes) {
          updates[`eventsPublic/${eventId}/alternativeRoutes`] = alternativeRoutes;
        }
        if (tourRoutes) {
          updates[`eventsPublic/${eventId}/tourRoutes`] = tourRoutes;
        }
      }

      if (isInviteOnly) {
        const invited = event.invitedUsers || {};
        Object.keys(invited).forEach((uid) => {
          updates[`eventsInvited/${uid}/${eventId}/isLive`] = true;
          updates[`eventsInvited/${uid}/${eventId}/liveStartedAt`] = now;
          updates[`eventsInvited/${uid}/${eventId}/status`] = "open";
          if (routeGPXUrl) {
            updates[`eventsInvited/${uid}/${eventId}/routeGPXUrl`] = routeGPXUrl;
          }
          if (alternativeRoutes) {
            updates[
              `eventsInvited/${uid}/${eventId}/alternativeRoutes`
            ] = alternativeRoutes;
          }
          if (tourRoutes) {
            updates[`eventsInvited/${uid}/${eventId}/tourRoutes`] = tourRoutes;
          }
        });
      }
    }

    await update(ref(db), updates);

    logEvent("events_live_start", "Evento colocado em modo LIVE", {
      eventId,
      adminUid,
      groupId,
    });
  },

  stopEventLive: async (eventId) => {
    if (!eventId) throw new Error("Evento inválido.");

    const adminUid = useAuthStore.getState().user?.uid || null;
    const now = Date.now();

    const eventsState = get().events || [];
    const cachedEvent = eventsState.find((e) => e.id === eventId) || null;
    let freshEvent = null;
    try {
      const snap = await getDb(ref(db, `/events/${eventId}`));
      freshEvent = snap.exists() ? snap.val() : null;
    } catch (e) {
      console.warn("stopEventLive: falha ao ler evento fresco", e);
    }
    const event = { ...(cachedEvent || {}), ...(freshEvent || {}), id: eventId };
    const groupId = event?.groupId || null;

    const updates = {
      [`events/${eventId}/isLive`]: false,
      [`events/${eventId}/liveFinishedAt`]: now,
    };

    // ✅ LIVE OFF → grupo limpa activeEventId (isto é o “OFF REAL”)
    if (groupId) {
      updates[`groups/${groupId}/activeEventId`] = null;
    }

    // ✅ limpar alertas quando LIVE termina
    updates[`events/${eventId}/alerts`] = null;
    if (groupId) {
      updates[`groups/${groupId}/alerts`] = null;
      // ✅ limpar localizações dos participantes quando LIVE termina
      updates[`groups/${groupId}/memberLocations`] = null;
    }

    if (event && Object.keys(event).length > 0) {
      const isInviteOnly = event.inviteOnly === true;
      const isPublicEvent =
        !isInviteOnly &&
        event.published === true &&
        event.isPublic === true;

      updates[`eventsLive/${eventId}`] = null;

      if (isPublicEvent) {
        updates[`eventsPublic/${eventId}/isLive`] = false;
        updates[`eventsPublic/${eventId}/liveFinishedAt`] = now;
      }

      if (isInviteOnly) {
        const invited = event.invitedUsers || {};
        Object.keys(invited).forEach((uid) => {
          updates[`eventsInvited/${uid}/${eventId}/isLive`] = false;
          updates[`eventsInvited/${uid}/${eventId}/liveFinishedAt`] = now;
        });
      }
    }

    await update(ref(db), updates);

    try {
      const panels = usePanelsStore.getState();
      if (panels?.clearEventRoute) panels.clearEventRoute(eventId);
      if (window.__clearEventRoute) window.__clearEventRoute();
    } catch {}

    logEvent("events_live_stop", "Evento retirado de modo LIVE", {
      eventId,
      adminUid,
      groupId,
    });
  },

  // -----------------------------------------------------------
  // FINALIZAR EVENTO (TODOS PRESENTES)
  // -----------------------------------------------------------
  finalizeEventAllPresent: async (eventId) => {
    if (!eventId) return;

    const adminUid = useAuthStore.getState().user?.uid || null;
    const regs = get().eventRegistrations[eventId] || {};
    const eventsState = get().events || [];
    const event = eventsState.find((e) => e.id === eventId) || null;
    const groupId = event?.groupId || null;

    const now = Date.now();
    const updates = {};

    Object.entries(regs).forEach(([uid, reg]) => {
      const status = reg?.status;

      if (status === "confirmed") {
        updates[`eventRegistrations/${eventId}/${uid}/presence`] = true;
        updates[`eventRegistrations/${eventId}/${uid}/participated`] = true;
        updates[`eventRegistrations/${eventId}/${uid}/trophy`] = true;

        updates[`userEvents/${uid}/${eventId}/presence`] = true;
        updates[`userEvents/${uid}/${eventId}/participated`] = true;
        updates[`userEvents/${uid}/${eventId}/trophy`] = true;
      } else {
        const newStatus = status === "pending" ? "expired" : status;

        updates[`eventRegistrations/${eventId}/${uid}/status`] = newStatus;
        updates[`eventRegistrations/${eventId}/${uid}/presence`] = false;
        updates[`eventRegistrations/${eventId}/${uid}/participated`] = false;
        updates[`eventRegistrations/${eventId}/${uid}/trophy`] = false;

        updates[`userEvents/${uid}/${eventId}/status`] = newStatus;
        updates[`userEvents/${uid}/${eventId}/presence`] = false;
        updates[`userEvents/${uid}/${eventId}/participated`] = false;
        updates[`userEvents/${uid}/${eventId}/trophy`] = false;
      }
    });

    updates[`events/${eventId}/status`] = "finished";
    updates[`events/${eventId}/finishedAt`] = now;

    updates[`events/${eventId}/isLive`] = false;
    updates[`events/${eventId}/liveFinishedAt`] = now;
    updates[`eventsLive/${eventId}`] = null;

    if (groupId) {
      updates[`groups/${groupId}/activeEventId`] = null;
      // ✅ limpar localizações dos participantes quando LIVE termina
      updates[`groups/${groupId}/memberLocations`] = null;
    }

    if (event?.published === true && event?.isPublic === true && event?.inviteOnly !== true) {
      updates[`eventsPublic/${eventId}/isLive`] = false;
      updates[`eventsPublic/${eventId}/liveFinishedAt`] = now;
    }
    if (event?.inviteOnly === true) {
      Object.keys(event?.invitedUsers || {}).forEach((uid) => {
        updates[`eventsInvited/${uid}/${eventId}/isLive`] = false;
        updates[`eventsInvited/${uid}/${eventId}/liveFinishedAt`] = now;
      });
    }

    await update(ref(db), updates);

    get().stopListeningEventRegistrations(eventId);

    try {
      const panels = usePanelsStore.getState();
      if (panels?.clearEventRoute) panels.clearEventRoute(eventId);
      if (window.__clearEventRoute) window.__clearEventRoute();
    } catch {}

    logEvent(
      "events_finalize_all_present",
      "Evento finalizado (todos presentes)",
      { eventId, adminUid }
    );
  },

  // -----------------------------------------------------------
  // FINALIZAR EVENTO (LISTA MANUAL DE PRESENTES)
  // -----------------------------------------------------------
  finalizeEventWithPresenceList: async (eventId, presentUids) => {
    if (!eventId || !Array.isArray(presentUids)) return;

    const adminUid = useAuthStore.getState().user?.uid || null;
    const regs = get().eventRegistrations[eventId] || {};
    const eventsState = get().events || [];
    const event = eventsState.find((e) => e.id === eventId) || null;
    const groupId = event?.groupId || null;

    const now = Date.now();
    const presentSet = new Set(presentUids);

    const updates = {};

    Object.entries(regs).forEach(([uid, reg]) => {
      const status = reg?.status;
      const isPresent = presentSet.has(uid) && status === "confirmed";

      if (isPresent) {
        updates[`eventRegistrations/${eventId}/${uid}/presence`] = true;
        updates[`eventRegistrations/${eventId}/${uid}/participated`] = true;
        updates[`eventRegistrations/${eventId}/${uid}/trophy`] = true;

        updates[`userEvents/${uid}/${eventId}/presence`] = true;
        updates[`userEvents/${uid}/${eventId}/participated`] = true;
        updates[`userEvents/${uid}/${eventId}/trophy`] = true;
      } else {
        const newStatus =
          status === "confirmed"
            ? "absent"
            : status === "pending"
            ? "expired"
            : status;

        updates[`eventRegistrations/${eventId}/${uid}/status`] = newStatus;
        updates[`eventRegistrations/${eventId}/${uid}/presence`] = false;
        updates[`eventRegistrations/${eventId}/${uid}/participated`] = false;
        updates[`eventRegistrations/${eventId}/${uid}/trophy`] = false;

        updates[`userEvents/${uid}/${eventId}/status`] = newStatus;
        updates[`userEvents/${uid}/${eventId}/presence`] = false;
        updates[`userEvents/${uid}/${eventId}/participated`] = false;
        updates[`userEvents/${uid}/${eventId}/trophy`] = false;
      }
    });

    updates[`events/${eventId}/status`] = "finished";
    updates[`events/${eventId}/finishedAt`] = now;

    updates[`events/${eventId}/isLive`] = false;
    updates[`events/${eventId}/liveFinishedAt`] = now;
    updates[`eventsLive/${eventId}`] = null;

    if (groupId) {
      updates[`groups/${groupId}/activeEventId`] = null;
      updates[`groups/${groupId}/memberLocations`] = null;
    }


    if (event?.published === true && event?.isPublic === true && event?.inviteOnly !== true) {
      updates[`eventsPublic/${eventId}/isLive`] = false;
      updates[`eventsPublic/${eventId}/liveFinishedAt`] = now;
    }
    if (event?.inviteOnly === true) {
      Object.keys(event?.invitedUsers || {}).forEach((uid) => {
        updates[`eventsInvited/${uid}/${eventId}/isLive`] = false;
        updates[`eventsInvited/${uid}/${eventId}/liveFinishedAt`] = now;
      });
    }

    await update(ref(db), updates);

    get().stopListeningEventRegistrations(eventId);

    try {
      const panels = usePanelsStore.getState();
      if (panels?.clearEventRoute) panels.clearEventRoute(eventId);
      if (window.__clearEventRoute) window.__clearEventRoute();
    } catch {}

    logEvent(
      "events_finalize_presence_list",
      "Evento finalizado com lista manual",
      { eventId, adminUid, presentCount: presentUids.length }
    );
  },

  // -----------------------------------------------------------
  // 🆕 Trilhos alternativos do evento
  // -----------------------------------------------------------
  addAlternativeRoute: async (eventId, { name, routeGPXUrl }) => {
    if (!eventId || !routeGPXUrl) return;

    const altRef = push(ref(db, `/events/${eventId}/alternativeRoutes`));
    const altId = altRef.key;

    const payload = {
      name: name || "Alternativo",
      routeGPXUrl,
      createdAt: Date.now(),
    };

    await update(ref(db), {
      [`events/${eventId}/alternativeRoutes/${altId}`]: payload,
    });

    try {
      const eventsState = get().events || [];
      const event = eventsState.find((e) => e.id === eventId) || null;
      if (event) {
        const isInviteOnly = event.inviteOnly === true;
        const isPublicEvent =
          !isInviteOnly &&
          event.published === true &&
          event.isPublic === true;

        const indexUpdates = {};
        if (isPublicEvent) {
          indexUpdates[
            `eventsPublic/${eventId}/alternativeRoutes/${altId}`
          ] = payload;
        }
        if (isInviteOnly) {
          const invited = event.invitedUsers || {};
          Object.keys(invited).forEach((uid) => {
            indexUpdates[
              `eventsInvited/${uid}/${eventId}/alternativeRoutes/${altId}`
            ] = payload;
          });
        }
        if (event.isLive === true) {
          indexUpdates[`eventsLive/${eventId}/alternativeRoutes/${altId}`] =
            payload;
          indexUpdates[`eventsLive/${eventId}/updatedAt`] = Date.now();
        }
        if (Object.keys(indexUpdates).length > 0) {
          await update(ref(db), indexUpdates);
        }
      }
    } catch (e) {
      console.warn("Falha a sincronizar altRoutes em índices públicos:", e);
    }

    logEvent("events_alt_route_add", "Trilho alternativo adicionado", {
      eventId,
      altId,
    });

    return altId;
  },

  setEventRoute: async (eventId, { routeGPXUrl, name }) => {
    if (!eventId || !routeGPXUrl) return;
    const updates = {
      [`events/${eventId}/routeGPXUrl`]: routeGPXUrl,
      [`events/${eventId}/routeName`]: name || null,
    };
    await update(ref(db), updates);

    try {
      const eventsState = get().events || [];
      const event = eventsState.find((e) => e.id === eventId) || null;
      if (event) {
        const isInviteOnly = event.inviteOnly === true;
        const isPublicEvent =
          !isInviteOnly &&
          event.published === true &&
          event.isPublic === true;

        const indexUpdates = {};
        if (isPublicEvent) {
          indexUpdates[`eventsPublic/${eventId}/routeGPXUrl`] = routeGPXUrl;
          indexUpdates[`eventsPublic/${eventId}/routeName`] = name || null;
        }
        if (isInviteOnly) {
          const invited = event.invitedUsers || {};
          Object.keys(invited).forEach((uid) => {
            indexUpdates[
              `eventsInvited/${uid}/${eventId}/routeGPXUrl`
            ] = routeGPXUrl;
            indexUpdates[
              `eventsInvited/${uid}/${eventId}/routeName`
            ] = name || null;
          });
        }
        if (event.isLive === true) {
          indexUpdates[`eventsLive/${eventId}/routeGPXUrl`] = routeGPXUrl;
          indexUpdates[`eventsLive/${eventId}/updatedAt`] = Date.now();
        }
        if (Object.keys(indexUpdates).length > 0) {
          await update(ref(db), indexUpdates);
        }
      }
    } catch (e) {
      console.warn("Falha a sincronizar routeGPXUrl em índices públicos:", e);
    }

    logEvent("events_route_set", "Trilho principal atualizado", {
      eventId,
      name: updates[`events/${eventId}/routeName`],
    });
  },

  removeAlternativeRoute: async (eventId, altId) => {
    if (!eventId || !altId) return;
    await update(ref(db), {
      [`events/${eventId}/alternativeRoutes/${altId}`]: null,
    });

    try {
      const eventsState = get().events || [];
      const event = eventsState.find((e) => e.id === eventId) || null;
      if (event) {
        const isInviteOnly = event.inviteOnly === true;
        const isPublicEvent =
          !isInviteOnly &&
          event.published === true &&
          event.isPublic === true;

        const indexUpdates = {};
        if (isPublicEvent) {
          indexUpdates[
            `eventsPublic/${eventId}/alternativeRoutes/${altId}`
          ] = null;
        }
        if (isInviteOnly) {
          const invited = event.invitedUsers || {};
          Object.keys(invited).forEach((uid) => {
            indexUpdates[
              `eventsInvited/${uid}/${eventId}/alternativeRoutes/${altId}`
            ] = null;
          });
        }
        if (event.isLive === true) {
          indexUpdates[`eventsLive/${eventId}/alternativeRoutes/${altId}`] = null;
          indexUpdates[`eventsLive/${eventId}/updatedAt`] = Date.now();
        }
        if (Object.keys(indexUpdates).length > 0) {
          await update(ref(db), indexUpdates);
        }
      }
    } catch (e) {
      console.warn("Falha a sincronizar remoção altRoutes nos índices:", e);
    }

    logEvent("events_alt_route_remove", "Trilho alternativo removido", {
      eventId,
      altId,
    });
  },

  // -----------------------------------------------------------
  // 🆕 Rotas tour do evento
  // -----------------------------------------------------------
  addTourRoute: async (eventId, { name, routeGPXUrl }) => {
    if (!eventId || !routeGPXUrl) return;

    const tourRef = push(ref(db, `/events/${eventId}/tourRoutes`));
    const tourId = tourRef.key;

    const payload = {
      name: name || "Rota tour",
      routeGPXUrl,
      createdAt: Date.now(),
    };

    await update(ref(db), {
      [`events/${eventId}/tourRoutes/${tourId}`]: payload,
    });

    try {
      const eventsState = get().events || [];
      const event = eventsState.find((e) => e.id === eventId) || null;
      if (event) {
        const isInviteOnly = event.inviteOnly === true;
        const isPublicEvent =
          !isInviteOnly &&
          event.published === true &&
          event.isPublic === true;

        const indexUpdates = {};
        if (isPublicEvent) {
          indexUpdates[`eventsPublic/${eventId}/tourRoutes/${tourId}`] =
            payload;
        }
        if (isInviteOnly) {
          const invited = event.invitedUsers || {};
          Object.keys(invited).forEach((uid) => {
            indexUpdates[
              `eventsInvited/${uid}/${eventId}/tourRoutes/${tourId}`
            ] = payload;
          });
        }
        if (event.isLive === true) {
          indexUpdates[`eventsLive/${eventId}/tourRoutes/${tourId}`] = payload;
          indexUpdates[`eventsLive/${eventId}/updatedAt`] = Date.now();
        }
        if (Object.keys(indexUpdates).length > 0) {
          await update(ref(db), indexUpdates);
        }
      }
    } catch (e) {
      console.warn("Falha a sincronizar tourRoutes em índices públicos:", e);
    }

    logEvent("events_tour_route_add", "Rota tour adicionada", {
      eventId,
      tourId,
    });

    return tourId;
  },

  removeTourRoute: async (eventId, tourId) => {
    if (!eventId || !tourId) return;
    await update(ref(db), {
      [`events/${eventId}/tourRoutes/${tourId}`]: null,
    });

    try {
      const eventsState = get().events || [];
      const event = eventsState.find((e) => e.id === eventId) || null;
      if (event) {
        const isInviteOnly = event.inviteOnly === true;
        const isPublicEvent =
          !isInviteOnly &&
          event.published === true &&
          event.isPublic === true;

        const indexUpdates = {};
        if (isPublicEvent) {
          indexUpdates[`eventsPublic/${eventId}/tourRoutes/${tourId}`] = null;
        }
        if (isInviteOnly) {
          const invited = event.invitedUsers || {};
          Object.keys(invited).forEach((uid) => {
            indexUpdates[
              `eventsInvited/${uid}/${eventId}/tourRoutes/${tourId}`
            ] = null;
          });
        }
        if (event.isLive === true) {
          indexUpdates[`eventsLive/${eventId}/tourRoutes/${tourId}`] = null;
          indexUpdates[`eventsLive/${eventId}/updatedAt`] = Date.now();
        }
        if (Object.keys(indexUpdates).length > 0) {
          await update(ref(db), indexUpdates);
        }
      }
    } catch (e) {
      console.warn("Falha a sincronizar remoção tourRoutes nos índices:", e);
    }

    logEvent("events_tour_route_remove", "Rota tour removida", {
      eventId,
      tourId,
    });
  },
}));
